/*
vivere 0.6.3
Copyright © 2024 Tiniest Fox, LLC
 */
var Timer = {
    time(message, callback) {
        const start = performance.now();
        callback();
        const time = performance.now() - start;
        let method;
        if (time >= 100)
            method = 'warn';
        else
            method = 'log';
        console[method](`Vivere | ${message}: ${time.toFixed(2)}ms`);
    },
};

class VivereError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'VivereError';
    }
}

class Printer {
    constructor(value, except, only) {
        this.value = value;
        this.except = except;
        this.only = only;
    }
    get typeLabel() {
        const { value } = this;
        if (value == null)
            return 'null';
        if (typeof value === 'object') {
            if (value.$name != null)
                return value.$name;
            if (value.constructor != null)
                return value.constructor.name;
        }
        return typeof value;
    }
    get properties() {
        const { except, only, value } = this;
        if (value == null)
            return [];
        return Object.entries(value).sort().map(([k, v]) => {
            if ((!only || only.includes(k)) && (!except || !except.includes(k)))
                return { key: k, value: v };
            return null;
        }).filter((v) => v != null);
    }
    get propertiesString() {
        const { properties, valueString } = this;
        return properties
            .filter((p) => !p.key.startsWith('$'))
            .map((p) => `  · ${p.key}\n\t  ↳ ${valueString(p.value)}`)
            .join('\n\t  ');
    }
    valueString(val) {
        if (val == null)
            return 'null';
        if (Array.isArray(val))
            return '[]';
        if (typeof val === 'function')
            return 'f()';
        return JSON.stringify(val);
    }
    print() {
        const { typeLabel, value } = this;
        if (typeLabel === 'string')
            return `↳ "${value}"`;
        if (typeLabel === 'number' || typeLabel === 'number')
            return `↳ ${value}`;
        const { propertiesString } = this;
        return `${typeLabel}
  ${propertiesString}`;
    }
}
var Printer$1 = {
    print(value, except, only) {
        return new Printer(value, except, only).print();
    },
};

class DirectiveError extends VivereError {
    constructor(message, directive, error) {
        let errorMessage = `${message}\n\n  ${Printer$1.print(directive.component)}\n\n  ${Printer$1.print(directive, [], ['key', 'modifiers', 'expression'])}\n\n`;
        if (error != null)
            errorMessage += `  ${error}`;
        super(errorMessage);
    }
}

class Directive {
    constructor(element, name, expression, component, renderController) {
        if (element instanceof HTMLElement && element.dataset[Directive.DATA_SUSPEND_PARSING] === 'true')
            return;
        this.component = component;
        this.renderController = renderController;
        this.element = element;
        this.expression = expression;
        let key;
        if (name.includes(':')) {
            [, ...key] = name.split(':');
            this.rawKey = key === null || key === void 0 ? void 0 : key.join(':');
            [this.key, ...this.modifiers] = this.rawKey.split('.');
        }
        else
            [, ...this.modifiers] = name.split('.');
        if (!this.key && this.requiresKey())
            throw new DirectiveError(`A key is required for ${this.id()} directives`, this);
        if (this.id() == null)
            throw new DirectiveError('Directives must have an identifier', this);
        if (this.forComponent() && !this.onComponent())
            throw new DirectiveError(`${name} only applies to component roots`, this);
        if (this.requiresComponent() && this.component == null)
            throw new DirectiveError(`${name} requires a component`, this);
        if (this.component != null)
            this.component.$directives.add(this);
        this.parse();
        this.element.removeAttribute(name);
    }
    parse() {
    }
    evaluate() {
    }
    destroy() {
    }
    dehydrate() {
        if (this.shouldRehydrate()) {
            let attributeName = this.id();
            if (this.key)
                attributeName += `:${this.key}`;
            if (this.modifiers && this.modifiers.length)
                attributeName += `.${this.modifiers.join('.')}`;
            this.element.setAttribute(attributeName, this.expression);
        }
    }
    id() {
        return this.constructor.id;
    }
    forComponent() {
        return this.constructor.forComponent;
    }
    requiresComponent() {
        return this.constructor.requiresComponent;
    }
    requiresKey() {
        return this.constructor.requiresKey;
    }
    onComponent() {
        return this.component != null && this.element === this.component.$element;
    }
    shouldRehydrate() {
        return this.constructor.shouldRehydrate;
    }
    suspendParsing(element = null) {
        const $element = element || this.element;
        if ($element instanceof HTMLElement)
            $element.dataset[Directive.DATA_SUSPEND_PARSING] = 'true';
    }
    resumeParsing(element = null) {
        const $element = element || this.element;
        if ($element instanceof HTMLElement)
            delete $element.dataset[Directive.DATA_SUSPEND_PARSING];
    }
}
Directive.requiresKey = false;
Directive.shouldRehydrate = true;
Directive.DATA_SUSPEND_PARSING = 'suspendVivereParsing';

class Watcher {
    constructor(context, callback) {
        this.context = context;
        this.callback = callback;
    }
    static watch(context, callback, watch) {
        const { current } = Watcher;
        Watcher.current = new Watcher(context, callback);
        watch();
        Watcher.current = current;
    }
}

/**
 * @implements {IHooks}
 */
class Hooks {
	/**
	 * @callback HookCallback
	 * @this {*|Jsep} this
	 * @param {Jsep} env
	 * @returns: void
	 */
	/**
	 * Adds the given callback to the list of callbacks for the given hook.
	 *
	 * The callback will be invoked when the hook it is registered for is run.
	 *
	 * One callback function can be registered to multiple hooks and the same hook multiple times.
	 *
	 * @param {string|object} name The name of the hook, or an object of callbacks keyed by name
	 * @param {HookCallback|boolean} callback The callback function which is given environment variables.
	 * @param {?boolean} [first=false] Will add the hook to the top of the list (defaults to the bottom)
	 * @public
	 */
	add(name, callback, first) {
		if (typeof arguments[0] != 'string') {
			// Multiple hook callbacks, keyed by name
			for (let name in arguments[0]) {
				this.add(name, arguments[0][name], arguments[1]);
			}
		}
		else {
			(Array.isArray(name) ? name : [name]).forEach(function (name) {
				this[name] = this[name] || [];

				if (callback) {
					this[name][first ? 'unshift' : 'push'](callback);
				}
			}, this);
		}
	}

	/**
	 * Runs a hook invoking all registered callbacks with the given environment variables.
	 *
	 * Callbacks will be invoked synchronously and in the order in which they were registered.
	 *
	 * @param {string} name The name of the hook.
	 * @param {Object<string, any>} env The environment variables of the hook passed to all callbacks registered.
	 * @public
	 */
	run(name, env) {
		this[name] = this[name] || [];
		this[name].forEach(function (callback) {
			callback.call(env && env.context ? env.context : env, env);
		});
	}
}

/**
 * @implements {IPlugins}
 */
class Plugins {
	constructor(jsep) {
		this.jsep = jsep;
		this.registered = {};
	}

	/**
	 * @callback PluginSetup
	 * @this {Jsep} jsep
	 * @returns: void
	 */
	/**
	 * Adds the given plugin(s) to the registry
	 *
	 * @param {object} plugins
	 * @param {string} plugins.name The name of the plugin
	 * @param {PluginSetup} plugins.init The init function
	 * @public
	 */
	register(...plugins) {
		plugins.forEach((plugin) => {
			if (typeof plugin !== 'object' || !plugin.name || !plugin.init) {
				throw new Error('Invalid JSEP plugin format');
			}
			if (this.registered[plugin.name]) {
				// already registered. Ignore.
				return;
			}
			plugin.init(this.jsep);
			this.registered[plugin.name] = plugin;
		});
	}
}

//     JavaScript Expression Parser (JSEP) 1.0.2

class Jsep {
	/**
	 * @returns {string}
	 */
	static get version() {
		// To be filled in by the template
		return '1.0.2';
	}

	/**
	 * @returns {string}
	 */
	static toString() {
		return 'JavaScript Expression Parser (JSEP) v' + Jsep.version;
	};

	// ==================== CONFIG ================================
	/**
	 * @method addUnaryOp
	 * @param {string} op_name The name of the unary op to add
	 * @returns {Jsep}
	 */
	static addUnaryOp(op_name) {
		Jsep.max_unop_len = Math.max(op_name.length, Jsep.max_unop_len);
		Jsep.unary_ops[op_name] = 1;
		return Jsep;
	}

	/**
	 * @method jsep.addBinaryOp
	 * @param {string} op_name The name of the binary op to add
	 * @param {number} precedence The precedence of the binary op (can be a float). Higher number = higher precedence
	 * @returns {Jsep}
	 */
	static addBinaryOp(op_name, precedence) {
		Jsep.max_binop_len = Math.max(op_name.length, Jsep.max_binop_len);
		Jsep.binary_ops[op_name] = precedence;
		return Jsep;
	}

	/**
	 * @method addIdentifierChar
	 * @param {string} char The additional character to treat as a valid part of an identifier
	 * @returns {Jsep}
	 */
	static addIdentifierChar(char) {
		Jsep.additional_identifier_chars.add(char);
		return Jsep;
	}

	/**
	 * @method addLiteral
	 * @param {string} literal_name The name of the literal to add
	 * @param {*} literal_value The value of the literal
	 * @returns {Jsep}
	 */
	static addLiteral(literal_name, literal_value) {
		Jsep.literals[literal_name] = literal_value;
		return Jsep;
	}

	/**
	 * @method removeUnaryOp
	 * @param {string} op_name The name of the unary op to remove
	 * @returns {Jsep}
	 */
	static removeUnaryOp(op_name) {
		delete Jsep.unary_ops[op_name];
		if (op_name.length === Jsep.max_unop_len) {
			Jsep.max_unop_len = Jsep.getMaxKeyLen(Jsep.unary_ops);
		}
		return Jsep;
	}

	/**
	 * @method removeAllUnaryOps
	 * @returns {Jsep}
	 */
	static removeAllUnaryOps() {
		Jsep.unary_ops = {};
		Jsep.max_unop_len = 0;

		return Jsep;
	}

	/**
	 * @method removeIdentifierChar
	 * @param {string} char The additional character to stop treating as a valid part of an identifier
	 * @returns {Jsep}
	 */
	static removeIdentifierChar(char) {
		Jsep.additional_identifier_chars.delete(char);
		return Jsep;
	}

	/**
	 * @method removeBinaryOp
	 * @param {string} op_name The name of the binary op to remove
	 * @returns {Jsep}
	 */
	static removeBinaryOp(op_name) {
		delete Jsep.binary_ops[op_name];

		if (op_name.length === Jsep.max_binop_len) {
			Jsep.max_binop_len = Jsep.getMaxKeyLen(Jsep.binary_ops);
		}

		return Jsep;
	}

	/**
	 * @method removeAllBinaryOps
	 * @returns {Jsep}
	 */
	static removeAllBinaryOps() {
		Jsep.binary_ops = {};
		Jsep.max_binop_len = 0;

		return Jsep;
	}

	/**
	 * @method removeLiteral
	 * @param {string} literal_name The name of the literal to remove
	 * @returns {Jsep}
	 */
	static removeLiteral(literal_name) {
		delete Jsep.literals[literal_name];
		return Jsep;
	}

	/**
	 * @method removeAllLiterals
	 * @returns {Jsep}
	 */
	static removeAllLiterals() {
		Jsep.literals = {};

		return Jsep;
	}
	// ==================== END CONFIG ============================


	/**
	 * @returns {string}
	 */
	get char() {
		return this.expr.charAt(this.index);
	}

	/**
	 * @returns {number}
	 */
	get code() {
		return this.expr.charCodeAt(this.index);
	};


	/**
	 * @param {string} expr a string with the passed in express
	 * @returns Jsep
	 */
	constructor(expr) {
		// `index` stores the character number we are currently at
		// All of the gobbles below will modify `index` as we move along
		this.expr = expr;
		this.index = 0;
	}

	/**
	 * static top-level parser
	 * @returns {jsep.Expression}
	 */
	static parse(expr) {
		return (new Jsep(expr)).parse();
	}

	/**
	 * Get the longest key length of any object
	 * @param {object} obj
	 * @returns {number}
	 */
	static getMaxKeyLen(obj) {
		return Math.max(0, ...Object.keys(obj).map(k => k.length));
	}

	/**
	 * `ch` is a character code in the next three functions
	 * @param {number} ch
	 * @returns {boolean}
	 */
	static isDecimalDigit(ch) {
		return (ch >= 48 && ch <= 57); // 0...9
	}

	/**
	 * Returns the precedence of a binary operator or `0` if it isn't a binary operator. Can be float.
	 * @param {string} op_val
	 * @returns {number}
	 */
	static binaryPrecedence(op_val) {
		return Jsep.binary_ops[op_val] || 0;
	}

	/**
	 * Looks for start of identifier
	 * @param {number} ch
	 * @returns {boolean}
	 */
	static isIdentifierStart(ch) {
		return  (ch >= 65 && ch <= 90) || // A...Z
			(ch >= 97 && ch <= 122) || // a...z
			(ch >= 128 && !Jsep.binary_ops[String.fromCharCode(ch)]) || // any non-ASCII that is not an operator
			(Jsep.additional_identifier_chars.has(String.fromCharCode(ch))); // additional characters
	}

	/**
	 * @param {number} ch
	 * @returns {boolean}
	 */
	static isIdentifierPart(ch) {
		return Jsep.isIdentifierStart(ch) || Jsep.isDecimalDigit(ch);
	}

	/**
	 * throw error at index of the expression
	 * @param {string} message
	 * @throws
	 */
	throwError(message) {
		const error = new Error(message + ' at character ' + this.index);
		error.index = this.index;
		error.description = message;
		throw error;
	}

	/**
	 * Run a given hook
	 * @param {string} name
	 * @param {jsep.Expression|false} [node]
	 * @returns {?jsep.Expression}
	 */
	runHook(name, node) {
		if (Jsep.hooks[name]) {
			const env = { context: this, node };
			Jsep.hooks.run(name, env);
			return env.node;
		}
		return node;
	}

	/**
	 * Runs a given hook until one returns a node
	 * @param {string} name
	 * @returns {?jsep.Expression}
	 */
	searchHook(name) {
		if (Jsep.hooks[name]) {
			const env = { context: this };
			Jsep.hooks[name].find(function (callback) {
				callback.call(env.context, env);
				return env.node;
			});
			return env.node;
		}
	}

	/**
	 * Push `index` up to the next non-space character
	 */
	gobbleSpaces() {
		let ch = this.code;
		// Whitespace
		while (ch === Jsep.SPACE_CODE
		|| ch === Jsep.TAB_CODE
		|| ch === Jsep.LF_CODE
		|| ch === Jsep.CR_CODE) {
			ch = this.expr.charCodeAt(++this.index);
		}
		this.runHook('gobble-spaces');
	}

	/**
	 * Top-level method to parse all expressions and returns compound or single node
	 * @returns {jsep.Expression}
	 */
	parse() {
		this.runHook('before-all');
		const nodes = this.gobbleExpressions();

		// If there's only one expression just try returning the expression
		const node = nodes.length === 1
		  ? nodes[0]
			: {
				type: Jsep.COMPOUND,
				body: nodes
			};
		return this.runHook('after-all', node);
	}

	/**
	 * top-level parser (but can be reused within as well)
	 * @param {number} [untilICode]
	 * @returns {jsep.Expression[]}
	 */
	gobbleExpressions(untilICode) {
		let nodes = [], ch_i, node;

		while (this.index < this.expr.length) {
			ch_i = this.code;

			// Expressions can be separated by semicolons, commas, or just inferred without any
			// separators
			if (ch_i === Jsep.SEMCOL_CODE || ch_i === Jsep.COMMA_CODE) {
				this.index++; // ignore separators
			}
			else {
				// Try to gobble each expression individually
				if (node = this.gobbleExpression()) {
					nodes.push(node);
					// If we weren't able to find a binary expression and are out of room, then
					// the expression passed in probably has too much
				}
				else if (this.index < this.expr.length) {
					if (ch_i === untilICode) {
						break;
					}
					this.throwError('Unexpected "' + this.char + '"');
				}
			}
		}

		return nodes;
	}

	/**
	 * The main parsing function.
	 * @returns {?jsep.Expression}
	 */
	gobbleExpression() {
		const node = this.searchHook('gobble-expression') || this.gobbleBinaryExpression();
		this.gobbleSpaces();

		return this.runHook('after-expression', node);
	}

	/**
	 * Search for the operation portion of the string (e.g. `+`, `===`)
	 * Start by taking the longest possible binary operations (3 characters: `===`, `!==`, `>>>`)
	 * and move down from 3 to 2 to 1 character until a matching binary operation is found
	 * then, return that binary operation
	 * @returns {string|boolean}
	 */
	gobbleBinaryOp() {
		this.gobbleSpaces();
		let to_check = this.expr.substr(this.index, Jsep.max_binop_len);
		let tc_len = to_check.length;

		while (tc_len > 0) {
			// Don't accept a binary op when it is an identifier.
			// Binary ops that start with a identifier-valid character must be followed
			// by a non identifier-part valid character
			if (Jsep.binary_ops.hasOwnProperty(to_check) && (
				!Jsep.isIdentifierStart(this.code) ||
				(this.index + to_check.length < this.expr.length && !Jsep.isIdentifierPart(this.expr.charCodeAt(this.index + to_check.length)))
			)) {
				this.index += tc_len;
				return to_check;
			}
			to_check = to_check.substr(0, --tc_len);
		}
		return false;
	}

	/**
	 * This function is responsible for gobbling an individual expression,
	 * e.g. `1`, `1+2`, `a+(b*2)-Math.sqrt(2)`
	 * @returns {?jsep.BinaryExpression}
	 */
	gobbleBinaryExpression() {
		let node, biop, prec, stack, biop_info, left, right, i, cur_biop;

		// First, try to get the leftmost thing
		// Then, check to see if there's a binary operator operating on that leftmost thing
		// Don't gobbleBinaryOp without a left-hand-side
		left = this.gobbleToken();
		if (!left) {
			return left;
		}
		biop = this.gobbleBinaryOp();

		// If there wasn't a binary operator, just return the leftmost node
		if (!biop) {
			return left;
		}

		// Otherwise, we need to start a stack to properly place the binary operations in their
		// precedence structure
		biop_info = { value: biop, prec: Jsep.binaryPrecedence(biop)};

		right = this.gobbleToken();

		if (!right) {
			this.throwError("Expected expression after " + biop);
		}

		stack = [left, biop_info, right];

		// Properly deal with precedence using [recursive descent](http://www.engr.mun.ca/~theo/Misc/exp_parsing.htm)
		while ((biop = this.gobbleBinaryOp())) {
			prec = Jsep.binaryPrecedence(biop);

			if (prec === 0) {
				this.index -= biop.length;
				break;
			}

			biop_info = { value: biop, prec };

			cur_biop = biop;

			// Reduce: make a binary expression from the three topmost entries.
			while ((stack.length > 2) && (prec <= stack[stack.length - 2].prec)) {
				right = stack.pop();
				biop = stack.pop().value;
				left = stack.pop();
				node = {
					type: Jsep.BINARY_EXP,
					operator: biop,
					left,
					right
				};
				stack.push(node);
			}

			node = this.gobbleToken();

			if (!node) {
				this.throwError("Expected expression after " + cur_biop);
			}

			stack.push(biop_info, node);
		}

		i = stack.length - 1;
		node = stack[i];

		while (i > 1) {
			node = {
				type: Jsep.BINARY_EXP,
				operator: stack[i - 1].value,
				left: stack[i - 2],
				right: node
			};
			i -= 2;
		}

		return node;
	}

	/**
	 * An individual part of a binary expression:
	 * e.g. `foo.bar(baz)`, `1`, `"abc"`, `(a % 2)` (because it's in parenthesis)
	 * @returns {boolean|jsep.Expression}
	 */
	gobbleToken() {
		let ch, to_check, tc_len, node;

		this.gobbleSpaces();
		node = this.searchHook('gobble-token');
		if (node) {
			return this.runHook('after-token', node);
		}

		ch = this.code;

		if (Jsep.isDecimalDigit(ch) || ch === Jsep.PERIOD_CODE) {
			// Char code 46 is a dot `.` which can start off a numeric literal
			return this.gobbleNumericLiteral();
		}

		if (ch === Jsep.SQUOTE_CODE || ch === Jsep.DQUOTE_CODE) {
			// Single or double quotes
			node = this.gobbleStringLiteral();
		}
		else if (ch === Jsep.OBRACK_CODE) {
			node = this.gobbleArray();
		}
		else {
			to_check = this.expr.substr(this.index, Jsep.max_unop_len);
			tc_len = to_check.length;

			while (tc_len > 0) {
				// Don't accept an unary op when it is an identifier.
				// Unary ops that start with a identifier-valid character must be followed
				// by a non identifier-part valid character
				if (Jsep.unary_ops.hasOwnProperty(to_check) && (
					!Jsep.isIdentifierStart(this.code) ||
					(this.index + to_check.length < this.expr.length && !Jsep.isIdentifierPart(this.expr.charCodeAt(this.index + to_check.length)))
				)) {
					this.index += tc_len;
					const argument = this.gobbleToken();
					if (!argument) {
						this.throwError('missing unaryOp argument');
					}
					return this.runHook('after-token', {
						type: Jsep.UNARY_EXP,
						operator: to_check,
						argument,
						prefix: true
					});
				}

				to_check = to_check.substr(0, --tc_len);
			}

			if (Jsep.isIdentifierStart(ch)) {
				node = this.gobbleIdentifier();
				if (Jsep.literals.hasOwnProperty(node.name)) {
					node = {
						type: Jsep.LITERAL,
						value: Jsep.literals[node.name],
						raw: node.name,
					};
				}
				else if (node.name === Jsep.this_str) {
					node = { type: Jsep.THIS_EXP };
				}
			}
			else if (ch === Jsep.OPAREN_CODE) { // open parenthesis
				node = this.gobbleGroup();
			}
		}

		if (!node) {
			return this.runHook('after-token', false);
		}

		node = this.gobbleTokenProperty(node);
		return this.runHook('after-token', node);
	}

	/**
	 * Gobble properties of of identifiers/strings/arrays/groups.
	 * e.g. `foo`, `bar.baz`, `foo['bar'].baz`
	 * It also gobbles function calls:
	 * e.g. `Math.acos(obj.angle)`
	 * @param {jsep.Expression} node
	 * @returns {jsep.Expression}
	 */
	gobbleTokenProperty(node) {
		this.gobbleSpaces();

		let ch = this.code;
		while (ch === Jsep.PERIOD_CODE || ch === Jsep.OBRACK_CODE || ch === Jsep.OPAREN_CODE || ch === Jsep.QUMARK_CODE) {
			let optional;
			if (ch === Jsep.QUMARK_CODE) {
				if (this.expr.charCodeAt(this.index + 1) !== Jsep.PERIOD_CODE) {
					break;
				}
				optional = true;
				this.index += 2;
				this.gobbleSpaces();
				ch = this.code;
			}
			this.index++;

			if (ch === Jsep.OBRACK_CODE) {
				node = {
					type: Jsep.MEMBER_EXP,
					computed: true,
					object: node,
					property: this.gobbleExpression()
				};
				this.gobbleSpaces();
				ch = this.code;
				if (ch !== Jsep.CBRACK_CODE) {
					this.throwError('Unclosed [');
				}
				this.index++;
			}
			else if (ch === Jsep.OPAREN_CODE) {
				// A function call is being made; gobble all the arguments
				node = {
					type: Jsep.CALL_EXP,
					'arguments': this.gobbleArguments(Jsep.CPAREN_CODE),
					callee: node
				};
			}
			else if (ch === Jsep.PERIOD_CODE || optional) {
				if (optional) {
					this.index--;
				}
				this.gobbleSpaces();
				node = {
					type: Jsep.MEMBER_EXP,
					computed: false,
					object: node,
					property: this.gobbleIdentifier(),
				};
			}

			if (optional) {
				node.optional = true;
			} // else leave undefined for compatibility with esprima

			this.gobbleSpaces();
			ch = this.code;
		}

		return node;
	}

	/**
	 * Parse simple numeric literals: `12`, `3.4`, `.5`. Do this by using a string to
	 * keep track of everything in the numeric literal and then calling `parseFloat` on that string
	 * @returns {jsep.Literal}
	 */
	gobbleNumericLiteral() {
		let number = '', ch, chCode;

		while (Jsep.isDecimalDigit(this.code)) {
			number += this.expr.charAt(this.index++);
		}

		if (this.code === Jsep.PERIOD_CODE) { // can start with a decimal marker
			number += this.expr.charAt(this.index++);

			while (Jsep.isDecimalDigit(this.code)) {
				number += this.expr.charAt(this.index++);
			}
		}

		ch = this.char;

		if (ch === 'e' || ch === 'E') { // exponent marker
			number += this.expr.charAt(this.index++);
			ch = this.char;

			if (ch === '+' || ch === '-') { // exponent sign
				number += this.expr.charAt(this.index++);
			}

			while (Jsep.isDecimalDigit(this.code)) { // exponent itself
				number += this.expr.charAt(this.index++);
			}

			if (!Jsep.isDecimalDigit(this.expr.charCodeAt(this.index - 1)) ) {
				this.throwError('Expected exponent (' + number + this.char + ')');
			}
		}

		chCode = this.code;

		// Check to make sure this isn't a variable name that start with a number (123abc)
		if (Jsep.isIdentifierStart(chCode)) {
			this.throwError('Variable names cannot start with a number (' +
				number + this.char + ')');
		}
		else if (chCode === Jsep.PERIOD_CODE || (number.length === 1 && number.charCodeAt(0) === Jsep.PERIOD_CODE)) {
			this.throwError('Unexpected period');
		}

		return {
			type: Jsep.LITERAL,
			value: parseFloat(number),
			raw: number
		};
	}

	/**
	 * Parses a string literal, staring with single or double quotes with basic support for escape codes
	 * e.g. `"hello world"`, `'this is\nJSEP'`
	 * @returns {jsep.Literal}
	 */
	gobbleStringLiteral() {
		let str = '';
		let quote = this.expr.charAt(this.index++);
		let closed = false;

		while (this.index < this.expr.length) {
			let ch = this.expr.charAt(this.index++);

			if (ch === quote) {
				closed = true;
				break;
			}
			else if (ch === '\\') {
				// Check for all of the common escape codes
				ch = this.expr.charAt(this.index++);

				switch (ch) {
					case 'n': str += '\n'; break;
					case 'r': str += '\r'; break;
					case 't': str += '\t'; break;
					case 'b': str += '\b'; break;
					case 'f': str += '\f'; break;
					case 'v': str += '\x0B'; break;
					default : str += ch;
				}
			}
			else {
				str += ch;
			}
		}

		if (!closed) {
			this.throwError('Unclosed quote after "' + str + '"');
		}

		return {
			type: Jsep.LITERAL,
			value: str,
			raw: quote + str + quote
		};
	}

	/**
	 * Gobbles only identifiers
	 * e.g.: `foo`, `_value`, `$x1`
	 * Also, this function checks if that identifier is a literal:
	 * (e.g. `true`, `false`, `null`) or `this`
	 * @returns {jsep.Identifier}
	 */
	gobbleIdentifier() {
		let ch = this.code, start = this.index;

		if (Jsep.isIdentifierStart(ch)) {
			this.index++;
		}
		else {
			this.throwError('Unexpected ' + this.char);
		}

		while (this.index < this.expr.length) {
			ch = this.code;

			if (Jsep.isIdentifierPart(ch)) {
				this.index++;
			}
			else {
				break;
			}
		}
		return {
			type: Jsep.IDENTIFIER,
			name: this.expr.slice(start, this.index),
		};
	}

	/**
	 * Gobbles a list of arguments within the context of a function call
	 * or array literal. This function also assumes that the opening character
	 * `(` or `[` has already been gobbled, and gobbles expressions and commas
	 * until the terminator character `)` or `]` is encountered.
	 * e.g. `foo(bar, baz)`, `my_func()`, or `[bar, baz]`
	 * @param {number} termination
	 * @returns {jsep.Expression[]}
	 */
	gobbleArguments(termination) {
		const args = [];
		let closed = false;
		let separator_count = 0;

		while (this.index < this.expr.length) {
			this.gobbleSpaces();
			let ch_i = this.code;

			if (ch_i === termination) { // done parsing
				closed = true;
				this.index++;

				if (termination === Jsep.CPAREN_CODE && separator_count && separator_count >= args.length){
					this.throwError('Unexpected token ' + String.fromCharCode(termination));
				}

				break;
			}
			else if (ch_i === Jsep.COMMA_CODE) { // between expressions
				this.index++;
				separator_count++;

				if (separator_count !== args.length) { // missing argument
					if (termination === Jsep.CPAREN_CODE) {
						this.throwError('Unexpected token ,');
					}
					else if (termination === Jsep.CBRACK_CODE) {
						for (let arg = args.length; arg < separator_count; arg++) {
							args.push(null);
						}
					}
				}
			}
			else if (args.length !== separator_count && separator_count !== 0) {
				// NOTE: `&& separator_count !== 0` allows for either all commas, or all spaces as arguments
				this.throwError('Expected comma');
			}
			else {
				const node = this.gobbleExpression();

				if (!node || node.type === Jsep.COMPOUND) {
					this.throwError('Expected comma');
				}

				args.push(node);
			}
		}

		if (!closed) {
			this.throwError('Expected ' + String.fromCharCode(termination));
		}

		return args;
	}

	/**
	 * Responsible for parsing a group of things within parentheses `()`
	 * that have no identifier in front (so not a function call)
	 * This function assumes that it needs to gobble the opening parenthesis
	 * and then tries to gobble everything within that parenthesis, assuming
	 * that the next thing it should see is the close parenthesis. If not,
	 * then the expression probably doesn't have a `)`
	 * @returns {boolean|jsep.Expression}
	 */
	gobbleGroup() {
		this.index++;
		let nodes = this.gobbleExpressions(Jsep.CPAREN_CODE);
		if (this.code === Jsep.CPAREN_CODE) {
			this.index++;
			if (nodes.length === 1) {
				return nodes[0];
			}
			else if (!nodes.length) {
				return false;
			}
			else {
				return {
					type: Jsep.SEQUENCE_EXP,
					expressions: nodes,
				};
			}
		}
		else {
			this.throwError('Unclosed (');
		}
	}

	/**
	 * Responsible for parsing Array literals `[1, 2, 3]`
	 * This function assumes that it needs to gobble the opening bracket
	 * and then tries to gobble the expressions as arguments.
	 * @returns {jsep.ArrayExpression}
	 */
	gobbleArray() {
		this.index++;

		return {
			type: Jsep.ARRAY_EXP,
			elements: this.gobbleArguments(Jsep.CBRACK_CODE)
		};
	}
}

// Static fields:
const hooks = new Hooks();
Object.assign(Jsep, {
	hooks,
	plugins: new Plugins(Jsep),

	// Node Types
	// ----------
	// This is the full set of types that any JSEP node can be.
	// Store them here to save space when minified
	COMPOUND:        'Compound',
	SEQUENCE_EXP:    'SequenceExpression',
	IDENTIFIER:      'Identifier',
	MEMBER_EXP:      'MemberExpression',
	LITERAL:         'Literal',
	THIS_EXP:        'ThisExpression',
	CALL_EXP:        'CallExpression',
	UNARY_EXP:       'UnaryExpression',
	BINARY_EXP:      'BinaryExpression',
	ARRAY_EXP:       'ArrayExpression',

	TAB_CODE:    9,
	LF_CODE:     10,
	CR_CODE:     13,
	SPACE_CODE:  32,
	PERIOD_CODE: 46, // '.'
	COMMA_CODE:  44, // ','
	SQUOTE_CODE: 39, // single quote
	DQUOTE_CODE: 34, // double quotes
	OPAREN_CODE: 40, // (
	CPAREN_CODE: 41, // )
	OBRACK_CODE: 91, // [
	CBRACK_CODE: 93, // ]
	QUMARK_CODE: 63, // ?
	SEMCOL_CODE: 59, // ;
	COLON_CODE:  58, // :


	// Operations
	// ----------
	// Use a quickly-accessible map to store all of the unary operators
	// Values are set to `1` (it really doesn't matter)
	unary_ops: {
		'-': 1,
		'!': 1,
		'~': 1,
		'+': 1
	},

	// Also use a map for the binary operations but set their values to their
	// binary precedence for quick reference (higher number = higher precedence)
	// see [Order of operations](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Operator_Precedence)
	binary_ops: {
		'||': 1, '&&': 2, '|': 3, '^': 4, '&': 5,
		'==': 6, '!=': 6, '===': 6, '!==': 6,
		'<': 7, '>': 7, '<=': 7, '>=': 7,
		'<<': 8, '>>': 8, '>>>': 8,
		'+': 9, '-': 9,
		'*': 10, '/': 10, '%': 10
	},

	// Additional valid identifier chars, apart from a-z, A-Z and 0-9 (except on the starting char)
	additional_identifier_chars: new Set(['$', '_']),

	// Literals
	// ----------
	// Store the values to return for the various literals we may encounter
	literals: {
		'true': true,
		'false': false,
		'null': null
	},

	// Except for `this`, which is special. This could be changed to something like `'self'` as well
	this_str: 'this',
});
Jsep.max_unop_len = Jsep.getMaxKeyLen(Jsep.unary_ops);
Jsep.max_binop_len = Jsep.getMaxKeyLen(Jsep.binary_ops);

// Backward Compatibility:
const jsep = expr => (new Jsep(expr)).parse();
const staticMethods = Object.getOwnPropertyNames(Jsep);
staticMethods
	.forEach((m) => {
		if (jsep[m] === undefined && m !== 'prototype') {
			jsep[m] = Jsep[m];
		}
	});
jsep.Jsep = Jsep; // allows for const { Jsep } = require('jsep');

const CONDITIONAL_EXP = 'ConditionalExpression';

var ternary = {
	name: 'ternary',

	init(jsep) {
		// Ternary expression: test ? consequent : alternate
		jsep.hooks.add('after-expression', function gobbleTernary(env) {
			if (env.node && this.code === jsep.QUMARK_CODE) {
				this.index++;
				const test = env.node;
				const consequent = this.gobbleExpression();

				if (!consequent) {
					this.throwError('Expected expression');
				}

				this.gobbleSpaces();

				if (this.code === jsep.COLON_CODE) {
					this.index++;
					const alternate = this.gobbleExpression();

					if (!alternate) {
						this.throwError('Expected expression');
					}
					env.node = {
						type: CONDITIONAL_EXP,
						test,
						consequent,
						alternate,
					};
				}
				// if binary operator is custom-added (i.e. object plugin), then correct it to a ternary node:
				// Note: BinaryExpressions can be stacked (similar to 1 + 1 + 1), so we have to collapse the stack
				// Only do one level at a time so we can unroll as we pop the ternary stack
				else if (test.operator === ':') {
					// this happens when the alternate is a ternary
					if (!consequent.right) {
						this.throwError('Expected :');
					}
					const node = findLastBinaryNode(consequent);
					test.right = {
						type: CONDITIONAL_EXP,
						test: test.right,
						consequent: node.left,
						alternate: node === consequent ? node.right : {
							// temporary values because we still have to wait to pop the consequent...
							operator: ':',
							left: node.right,
							right: consequent.right,
						},
					};
					env.node = test;
				}
				else if (consequent.operator === ':') {
					convertBinaryToConditional(findLastBinaryNode(consequent), test);
					env.node = consequent;
				}
				else if (consequent.alternate) {
					// cleanup the temporary placeholder we made, now that we have the consequent
					let alternate = consequent.alternate;
					while (alternate.alternate) {
						alternate = alternate.alternate;
					}
					env.node = {
						type: CONDITIONAL_EXP,
						test,
						consequent,
						alternate: alternate.right,
					};
					delete alternate.operator;
					delete alternate.right;
					Object.assign(alternate, alternate.left);
				}
				else {
					this.throwError('Expected :');
				}
			}
		});

		/**
		 * @param {jsep.Expression} node
		 * @returns {jsep.Expression}
		 */
		function findLastBinaryNode(node) {
			while (node.left && node.left.operator === ':') {
				node = node.left;
			}
			return node;
		}

		/**
		 * @param {jsep.BinaryExpression} node
		 * @param {jsep.Expression} test
		 * @returns {jsep.ConditionalExpression}
		 */
		function convertBinaryToConditional(node, test) {
			node.type = CONDITIONAL_EXP;
			node.test = test;
			node.consequent = node.left;
			node.alternate = node.right;
			delete node.operator;
			delete node.left;
			delete node.right;
		}
	},
};

// Add default plugins:

jsep.plugins.register(ternary);

const PLUS_CODE = 43; // +
const MINUS_CODE = 45; // -

const plugin = {
	name: 'assignment',

	assignmentOperators: new Set([
		'=',
		'*=',
		'**=',
		'/=',
		'%=',
		'+=',
		'-=',
		'<<=',
		'>>=',
		'>>>=',
		'&=',
		'^=',
		'|=',
	]),
	updateOperators: [PLUS_CODE, MINUS_CODE],
	assignmentPrecedence: 0.9,

	init(jsep) {
		const updateNodeTypes = [jsep.IDENTIFIER, jsep.MEMBER_EXP];
		plugin.assignmentOperators.forEach(op => jsep.addBinaryOp(op, plugin.assignmentPrecedence));

		jsep.hooks.add('gobble-token', function gobbleUpdatePrefix(env) {
			const code = this.code;
			if (plugin.updateOperators.some(c => c === code && c === this.expr.charCodeAt(this.index + 1))) {
				this.index += 2;
				env.node = {
					type: 'UpdateExpression',
					operator: code === PLUS_CODE ? '++' : '--',
					argument: this.gobbleTokenProperty(this.gobbleIdentifier()),
					prefix: true,
				};
				if (!env.node.argument || !updateNodeTypes.includes(env.node.argument.type)) {
					this.throwError(`Unexpected ${env.node.operator}`);
				}
			}
		});

		jsep.hooks.add('after-token', function gobbleUpdatePostfix(env) {
			if (env.node) {
				const code = this.code;
				if (plugin.updateOperators.some(c => c === code && c === this.expr.charCodeAt(this.index + 1))) {
					if (!updateNodeTypes.includes(env.node.type)) {
						this.throwError(`Unexpected ${env.node.operator}`);
					}
					this.index += 2;
					env.node = {
						type: 'UpdateExpression',
						operator: code === PLUS_CODE ? '++' : '--',
						argument: env.node,
						prefix: false,
					};
				}
			}
		});

		jsep.hooks.add('after-expression', function gobbleAssignment(env) {
			if (env.node) {
				// Note: Binaries can be chained in a single expression to respect
				// operator precedence (i.e. a = b = 1 + 2 + 3)
				// Update all binary assignment nodes in the tree
				updateBinariessToAssignments(env.node);
			}
		});

		function updateBinariessToAssignments(node) {
			if (plugin.assignmentOperators.has(node.operator)) {
				node.type = 'AssignmentExpression';
				updateBinariessToAssignments(node.left);
				updateBinariessToAssignments(node.right);
			}
		}
	},
};

class EvaluatorError extends VivereError {
    constructor(message, object, expression, error) {
        let errorMessage = `${message}\n\n  ${Printer$1.print(object)}\n\n  Expression\n    ${Printer$1.print(expression)}\n\n`;
        if (error != null)
            errorMessage += `  ${error}`;
        super(errorMessage);
    }
}

jsep.plugins.register(plugin);
jsep.addBinaryOp('??', 0.1);
var ParseResult;
(function (ParseResult) {
    ParseResult[ParseResult["AssignmentExpressionExecuted"] = 0] = "AssignmentExpressionExecuted";
    ParseResult[ParseResult["EmptyExpression"] = 1] = "EmptyExpression";
})(ParseResult || (ParseResult = {}));
class ShallowParseResult {
    constructor(object, prop) {
        this.object = object;
        this.prop = prop;
    }
}
class ShallowCallResult {
    constructor(value) {
        this.value = value;
    }
}
const assignmentOperators = [
    '=', '*=', '**=',
    '/=', '%=',
    '+=', '-=',
    '<<=', '>>=', '>>>=',
    '&=', '^=', '|=',
];
const binaryOperators = [
    '||', '&&', '|', '^', '&',
    '==', '!=', '===', '!==',
    '<', '>', '<=', '>=', '<<', '>>', '>>>',
    '+', '-', '*', '/', '%',
];
const evaluateScript = (script, scope = {}) => {
    return Function(`"use strict"; ${script}`).bind(scope)();
};
let evaluateTree;
const evaluateAssignmentExpression = (caller, tree, options) => {
    const { left, operator, right, type } = tree;
    const leftValue = evaluateTree(caller, left, options, true);
    if (leftValue instanceof ShallowParseResult) {
        const rightValue = evaluateTree(caller, right, options);
        if (assignmentOperators.includes(operator)) {
            const propJSON = JSON.stringify(leftValue.prop);
            const valueJSON = JSON.stringify(rightValue);
            evaluateScript(`this[${propJSON}] ${operator} ${valueJSON}`, leftValue.object);
        }
        else
            throw new EvaluatorError(`Unhandled assignment expression: ${operator}`, caller, type);
    }
    else
        throw new EvaluatorError('Tried to assign to deeply parsed value', caller, type);
    return ParseResult.AssignmentExpressionExecuted;
};
const evaluateCallExpression = (caller, tree, options, shallow) => {
    const { callee, type } = tree;
    const args = tree.arguments;
    const calleeValues = evaluateTree(caller, callee, options, true);
    if (calleeValues instanceof ShallowParseResult) {
        const argsValues = args.map((arg) => evaluateTree(caller, arg, options));
        const returnValue = calleeValues.object[calleeValues.prop](...argsValues);
        if (shallow)
            return new ShallowCallResult(returnValue);
        return returnValue;
    }
    throw new EvaluatorError('Tried to invoke method on deeply parsed value', caller, type);
};
const evaluateCompound = (caller, tree, options, shallow) => {
    const { body } = tree;
    return body.map((exp) => evaluateTree(caller, exp, options, shallow));
};
const evaluateConditionalExpression = (caller, tree, options, shallow) => {
    const { alternate, consequent, test } = tree;
    const testValue = evaluateTree(caller, test, options);
    if (testValue)
        return evaluateTree(caller, consequent, options, shallow);
    return evaluateTree(caller, alternate, options, shallow);
};
const evaluateBinaryExpression = (caller, tree, options, shallow) => {
    const { left, operator, right, type } = tree;
    const leftValue = evaluateTree(caller, left, options);
    if (operator === '??')
        if (leftValue)
            return evaluateTree(caller, right, options, shallow);
        else if (shallow)
            return ParseResult.EmptyExpression;
        else
            return null;
    const rightValue = evaluateTree(caller, right, options);
    if (assignmentOperators.includes(operator))
        return evaluateAssignmentExpression(caller, tree, options);
    if (binaryOperators.includes(operator)) {
        const leftJSON = JSON.stringify(leftValue);
        const rightJSON = JSON.stringify(rightValue);
        return evaluateScript(`return ${leftJSON} ${operator} ${rightJSON}`);
    }
    throw new EvaluatorError(`Unhandled binary operator: ${operator}`, caller, type);
};
const evaluateUnaryExpression = (caller, tree, options) => {
    const { argument, operator, type } = tree;
    const argumentValue = evaluateTree(caller, argument, options);
    switch (operator) {
        case '!':
            return !argumentValue;
        default:
            throw new EvaluatorError(`Unhandled unary operator: ${operator}`, caller, type);
    }
};
const evaluateMemberExpression = (caller, tree, options, shallow) => {
    var _a;
    const { computed, object, optional, property } = tree;
    const $object = evaluateTree(caller, object, options);
    if (optional && $object == null)
        return null;
    if (computed) {
        const propKey = (_a = evaluateTree(options.component, property, options)) === null || _a === void 0 ? void 0 : _a.toString();
        if (shallow)
            return new ShallowParseResult($object, propKey);
        return $object[propKey];
    }
    return evaluateTree($object, property, options, shallow);
};
const evaluateIdentifier = (caller, tree, options, shallow) => {
    const { name } = tree;
    if (name === '$args' && options.$args)
        if (shallow)
            throw new EvaluatorError('Cannot shallow parse $args', caller, name);
        else
            return options.$args;
    if (shallow)
        return new ShallowParseResult(caller, name);
    return caller[name];
};
const evaluateLiteral = (tree) => tree.value;
evaluateTree = (caller, tree, options, shallow = false) => {
    const { type } = tree;
    switch (type) {
        case 'AssignmentExpression':
            return evaluateAssignmentExpression(caller, tree, options);
        case 'CallExpression':
            return evaluateCallExpression(caller, tree, options, shallow);
        case 'Compound':
            return evaluateCompound(caller, tree, options, shallow);
        case 'ConditionalExpression':
            return evaluateConditionalExpression(caller, tree, options, shallow);
        case 'BinaryExpression':
            return evaluateBinaryExpression(caller, tree, options, shallow);
        case 'UnaryExpression':
            return evaluateUnaryExpression(caller, tree, options);
        case 'MemberExpression':
            return evaluateMemberExpression(caller, tree, options, shallow);
        case 'Identifier':
            return evaluateIdentifier(caller, tree, options, shallow);
        case 'Literal':
            return evaluateLiteral(tree);
        default:
            throw new EvaluatorError(`Unknown node type: ${type}`, caller, '', null);
    }
};
const parse = (component, expression, executing = false, $args = []) => {
    try {
        const tree = jsep(expression);
        return evaluateTree(component, tree, { component, $args }, executing);
    }
    catch (error) {
        throw new EvaluatorError('Failed to parse expression', component, expression, error);
    }
};
var Evaluator = {
    assign(component, expression, value) {
        const result = parse(component, expression, true);
        if (result instanceof ShallowParseResult) {
            const { object: caller, prop: property } = result;
            caller[property] = value;
        }
        else
            throw new EvaluatorError('Cannot assign to deeply parsed expression', component, expression);
    },
    parse,
    parsePrimitive(expression) {
        const number = Number(expression);
        if (!Number.isNaN(number))
            return number;
        if (expression === 'true')
            return true;
        if (expression === 'false')
            return false;
        if (expression === 'null')
            return null;
        if (expression === 'undefined')
            return null;
        if (expression.match(/^(('.*')|(".*"))$/))
            return expression.slice(1, expression.length - 1);
        return undefined;
    },
    execute(component, expression, ...args) {
        const response = parse(component, expression, true, args);
        if (response === ParseResult.AssignmentExpressionExecuted)
            return undefined;
        if (response === ParseResult.EmptyExpression)
            return null;
        if (response instanceof ShallowCallResult)
            return response.value;
        if (response instanceof ShallowParseResult) {
            const { object: caller, prop: property } = response;
            return caller[property](args);
        }
        if (Array.isArray(response))
            response.forEach((resp) => {
                if (resp instanceof ShallowParseResult) {
                    const { object: caller, prop: property } = resp;
                    caller[property](args);
                }
            });
        return response;
    },
    compute(component, expression) {
        const response = parse(component, expression, true);
        if (response === ParseResult.AssignmentExpressionExecuted)
            throw new EvaluatorError('Do not assign when computing a property', component, expression);
        if (response === ParseResult.EmptyExpression)
            return null;
        if (response instanceof ShallowCallResult)
            return response.value;
        if (response instanceof ShallowParseResult) {
            const { object: caller, prop: property } = response;
            return caller[property];
        }
        return response;
    },
};

class DisplayDirective extends Directive {
    constructor() {
        super(...arguments);
        this.$dirty = true;
    }
    renderCallback() {
        this.dirty();
        this.component.$queueRender(this);
    }
    parseExpression() {
        const { component, expression, renderCallback } = this;
        let value;
        Watcher.watch(this, renderCallback.bind(this), () => {
            value = Evaluator.parse(component, expression);
        });
        return value;
    }
    evaluate() {
        let { renderController } = this;
        let shouldRender;
        if (renderController != null) {
            shouldRender = true;
            do {
                const ancestorShouldEvaluate = renderController.shouldRender();
                if (!ancestorShouldEvaluate)
                    renderController.awaitingRender.add(this);
                shouldRender = shouldRender && ancestorShouldEvaluate;
                renderController = renderController.renderController;
            } while (shouldRender && renderController != null);
        }
        else
            shouldRender = true;
        if (shouldRender) {
            const value = this.parseExpression();
            this.evaluateValue(value);
            this.lastValue = value;
        }
        this.clean();
    }
    evaluateValue(value) {
        throw new DirectiveError('Directives must implement `evaluateValue`', this);
    }
    dirty() {
        this.$dirty = true;
    }
    clean() {
        this.$dirty = false;
    }
}

var Utility = {
    camelCase(name) {
        return name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    },
    pascalCase(name) {
        if (name == null || name.length <= 0)
            return null;
        const camel = this.camelCase(name);
        return `${camel[0].toUpperCase()}${camel.slice(1)}`;
    },
    kebabCase(name) {
        return name
            .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
            .map((x) => x.toLowerCase())
            .join('-');
    },
    orderBy(array, keys, directions) {
        return [...array].sort((a, b) => {
            for (let i = 0; i < keys.length; i += 1) {
                const key = keys[i];
                const aVal = a[key];
                const bVal = b[key];
                const direction = directions[i];
                const ascending = direction === 'asc';
                if (aVal > bVal)
                    return ascending ? 1 : -1;
                if (bVal > aVal)
                    return ascending ? -1 : 1;
            }
            return 0;
        });
    },
    jsonCopy(value) {
        if (value == null)
            return null;
        return JSON.parse(JSON.stringify(value));
    },
};

class ToggableRenderController {
    constructor(shouldRender, renderController) {
        this.$shouldRender = false;
        this.$dirty = false;
        this.awaitingRender = new Set();
        this.renderController = renderController;
        this.$shouldRender = shouldRender;
    }
    setShouldRender(shouldRender) {
        this.$shouldRender = shouldRender;
        if (this.$shouldRender)
            this.awaitingRender.forEach((d) => {
                var _a;
                (_a = d.component) === null || _a === void 0 ? void 0 : _a.$queueRender(d);
                this.awaitingRender.delete(d);
            });
    }
    shouldRender() {
        return this.$shouldRender;
    }
}

const directiveRegex = /(?:([A-z_$0-9]+)|\(([A-z_$0-9]+), ([A-z_$0-9]+)\)) of ([A-z_$0-9[\]().?]+)/;
class ForDirective extends DisplayDirective {
    constructor() {
        super(...arguments);
        this.keyedElements = {};
        this.unkeyedElements = [];
    }
    parse() {
        const { element } = this;
        this.suspendParsing();
        this.placeholder = document.createComment('');
        this.parent = element.parentElement;
        this.parent.insertBefore(this.placeholder, element);
        this.parent.removeChild(element);
    }
    parseExpression() {
        const { component, expression, renderCallback } = this;
        let value;
        Watcher.watch(this, renderCallback.bind(this), () => {
            try {
                const [, iExp, $iExp, indexExpression, listExpression] = directiveRegex.exec(expression);
                const itemExpression = iExp || $iExp;
                value = {
                    list: Evaluator.parse(component, listExpression),
                    listExpression,
                    itemExpression,
                    indexExpression,
                };
            }
            catch (e) {
                throw new DirectiveError('Invalid v-for expression, must resemble `item of list`', this, e);
            }
        });
        return value;
    }
    evaluateValue(value) {
        const { component, element, keyedElements, unkeyedElements, parent, placeholder } = this;
        const { itemExpression, indexExpression, list, listExpression } = value;
        const isList = Array.isArray(list);
        const isObject = list && !isList && (typeof list === 'object');
        if (list && !isList && !isObject)
            throw new DirectiveError('v-for directive expected an array, and object, or null to be returned', this);
        if (isObject && !(indexExpression === null || indexExpression === void 0 ? void 0 : indexExpression.length))
            throw new DirectiveError('Invalid v-for expression, must resemble `(key, value) of object`', this);
        const renderedElements = [];
        let insertBefore = placeholder;
        if (list != null) {
            const keys = Object.keys(list);
            const listLength = keys.length;
            for (let i = listLength - 1; i >= 0; i -= 1) {
                const $key = keys[i];
                const item = list[$key];
                const key = isList ? this.itemKey(item) : $key;
                let cachedElement;
                if (key != null) {
                    cachedElement = keyedElements[key];
                    delete keyedElements[key];
                }
                else
                    cachedElement = unkeyedElements.pop();
                if (cachedElement == null) {
                    const el = element.cloneNode(true);
                    if (!el.hasAttribute('v-component'))
                        el.setAttribute('v-component', '');
                    if (isList) {
                        el.setAttribute(`v-pass:${Utility.kebabCase(itemExpression)}.list`, `${listExpression}[${i}]`);
                        if (indexExpression === null || indexExpression === void 0 ? void 0 : indexExpression.length)
                            el.setAttribute(`v-data:${Utility.kebabCase(indexExpression)}`, `${i}`);
                    }
                    else {
                        el.setAttribute(`v-pass:${Utility.kebabCase(indexExpression)}.list`, `${listExpression}['${key}']`);
                        el.setAttribute(`v-data:${Utility.kebabCase(itemExpression)}`, `${key}`);
                    }
                    this.resumeParsing(el);
                    const renderController = new ToggableRenderController(true, this.renderController);
                    component.$attachElement(el, parent, insertBefore, renderController);
                    insertBefore = el;
                    cachedElement = el.$component;
                }
                else {
                    if (isList) {
                        cachedElement.$pass(itemExpression, listExpression, i);
                        if (indexExpression === null || indexExpression === void 0 ? void 0 : indexExpression.length)
                            cachedElement.$set(indexExpression, `${i}`);
                    }
                    else {
                        cachedElement.$pass(indexExpression, listExpression, key);
                        cachedElement.$set(itemExpression, key);
                    }
                    const { $element } = cachedElement;
                    parent.insertBefore($element, insertBefore);
                    insertBefore = $element;
                    if (cachedElement.$renderController instanceof ToggableRenderController)
                        cachedElement.$renderController.setShouldRender(true);
                }
                renderedElements.push({ key, el: cachedElement });
            }
        }
        this.removeCachedElements();
        renderedElements.forEach((re) => {
            const { key, el } = re;
            if (key != null)
                keyedElements[key] = el;
            else
                unkeyedElements.push(el);
        });
    }
    itemKey(item) {
        if (item == null)
            return null;
        if (Array.isArray(item))
            return JSON.stringify(item);
        const props = ['key', 'id', 'name'];
        for (let i = 0; i < props.length; i += 1) {
            const prop = props[i];
            if (item[prop])
                return item[prop].toString();
        }
        return null;
    }
    removeCachedElements() {
        const { keyedElements, unkeyedElements } = this;
        Object.values(keyedElements).forEach((ke) => {
            ke.$element.remove();
            if (ke.$renderController instanceof ToggableRenderController)
                ke.$renderController.setShouldRender(false);
        });
        unkeyedElements.forEach((ue) => {
            ue.$element.remove();
            if (ue.$renderController instanceof ToggableRenderController)
                ue.$renderController.setShouldRender(false);
        });
    }
    dehydrate() {
        const { element, parent, placeholder } = this;
        this.removeCachedElements();
        parent.replaceChild(element, placeholder);
        if (element instanceof HTMLElement)
            delete element.dataset[Directive.DATA_SUSPEND_PARSING];
        super.dehydrate();
    }
    dirty() {
        [...this.unkeyedElements, ...Object.values(this.keyedElements)].forEach((vc) => {
            if (vc.$renderController != null)
                vc.$renderController.$dirty = true;
        });
        super.dirty();
    }
    clean() {
        [...this.unkeyedElements, ...Object.values(this.keyedElements)].forEach((vc) => {
            if (vc.$renderController != null)
                vc.$renderController.$dirty = false;
        });
        super.clean();
    }
}
ForDirective.id = 'v-for';

const $components = new Set();
var ComponentRegistry = {
    get components() {
        return $components;
    },
    track(component) {
        $components.add(component);
    },
    untrack(component) {
        $components.delete(component);
    },
};

class Registry {
    constructor() {
        this.$array = [];
        this.$map = {};
    }
    register(key, value) {
        let index = this.$array.indexOf(key);
        if (index < 0) {
            index = this.$array.length;
            this.$array.push(key);
        }
        this.$map[index] = value;
    }
    deregister(key) {
        const index = this.$array.indexOf(key);
        delete this.$map[index];
        this.$array.splice(index, 1);
    }
    get(key) {
        const index = this.$array.indexOf(key);
        return this.$map[index];
    }
    forEach(func) {
        const { $array, $map } = this;
        for (let i = 0; i < $array.length; i += 1) {
            const registryKey = $array[i];
            if (registryKey != null) {
                const value = $map[i];
                func(registryKey, value);
            }
        }
    }
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

let $dirty = false;
const $directives = new Set();
const $ticks = new Set();
const tick = () => {
    $ticks.forEach((t) => t());
    $ticks.clear();
};
const render = (shouldTick = true) => {
    Timer.time('Directives rendered :', () => {
        while ($directives.size) {
            const directivesArray = Array.from($directives);
            while (directivesArray.length) {
                const [directive] = directivesArray.splice(0, 1);
                const { renderController } = directive;
                if (renderController == null || !renderController.$dirty) {
                    directive.evaluate();
                    $directives.delete(directive);
                }
            }
        }
        $dirty = false;
    });
    if (shouldTick)
        tick();
};
const renderAndTick = () => {
    render(true);
};
const Renderer = {
    $forceTick() {
        tick();
    },
    $forceRender(shouldTick = true) {
        render(shouldTick);
    },
    $queueRender(directive) {
        $directives.add(directive);
        if (!$dirty) {
            window.requestAnimationFrame(renderAndTick);
            $dirty = true;
        }
    },
    $nextRender(func) {
        $ticks.add(func);
    },
};

const getStorage = (definition) => {
    const { type } = definition;
    switch (type) {
        case 'local':
            return window.localStorage;
        case 'session':
            return window.sessionStorage;
        default:
            throw new VivereError(`Trying to store data in unknown store type: ${type}`);
    }
};
const getStoreKey = (key, definition) => {
    const { modifier } = definition;
    let { version } = definition;
    version = version || 0;
    let storeKey = `vivere-store-v${version}-${key}`;
    if (modifier != null)
        storeKey = `${storeKey}-${modifier}`;
    return storeKey;
};
var Storage = {
    retrieve(key, definition) {
        const storeKey = getStoreKey(key, definition);
        const storage = getStorage(definition);
        const storedValue = storage.getItem(storeKey);
        if (storedValue == null || storedValue === 'undefined')
            return undefined;
        return JSON.parse(storedValue);
    },
    save(key, definition, value) {
        const storeKey = getStoreKey(key, definition);
        const storage = getStorage(definition);
        storage.setItem(storeKey, JSON.stringify(value));
    },
};

class ComponentError extends VivereError {
    constructor(message, component, error) {
        let errorMessage = `${message}\n\n  ${Printer$1.print(component)}\n\n`;
        if (error != null)
            errorMessage += `  ${error}`;
        super(errorMessage);
    }
}

class ReactiveObject {
    static makeValueReactive(listeners, object, key, value) {
        let reactive = null;
        if (value instanceof Reactive)
            reactive = value;
        else {
            reactive = new Reactive(object, value, null);
            object[key] = reactive;
        }
        if (listeners != null)
            reactive.registerHook(object, (oldValue) => {
                listeners.forEach((l) => l.report(Object.assign(Object.assign({}, object), { key: oldValue })));
            });
    }
    static setReactiveValue(listeners, target, p, value) {
        const currentValue = target[p];
        if (currentValue instanceof Reactive)
            currentValue.set(value, true);
        else if (value instanceof Reactive)
            target[p] = value;
        else
            this.makeValueReactive(listeners, target, p, value);
        return true;
    }
    constructor(object) {
        const listeners = new Set();
        Object.entries(object).forEach(([key, value]) => {
            ReactiveObject.makeValueReactive(listeners, object, key, value);
        });
        return new Proxy(object, {
            get(target, p) {
                const value = target[p];
                if (value instanceof Reactive)
                    return value.get();
                if (p === '$reactives')
                    return Object.assign({}, target);
                switch (p) {
                    case '$$registerListener':
                        return (listener) => {
                            listeners.add(listener);
                        };
                    case '$$report':
                        return (oldValue) => {
                            listeners.forEach((l) => l.report(oldValue));
                        };
                    case '$$reactiveObject':
                        return true;
                    case '$$reactiveProxy':
                        return true;
                    default:
                        if (value && value.bind)
                            return value.bind(target);
                        return value;
                }
            },
            set(target, p, value) {
                return ReactiveObject.setReactiveValue(listeners, target, p, value);
            },
        });
    }
}

class ReactiveArray {
    static makeArrayReactive(listeners, array) {
        for (let i = 0; i < array.length; i += 1) {
            const value = array[i];
            ReactiveObject.makeValueReactive(listeners, array, i, value);
        }
    }
    constructor(array) {
        const listeners = new Set();
        ReactiveArray.makeArrayReactive(listeners, array);
        return new Proxy(array, {
            get(target, p) {
                const value = target[p];
                if (value instanceof Reactive)
                    return value.get();
                if (p === '$reactives')
                    return [...target];
                switch (p) {
                    case 'push':
                    case 'unshift':
                        return (...args) => {
                            const oldValue = [...target];
                            const reactiveArgs = args.map((a) => {
                                if (a instanceof Reactive)
                                    return a;
                                return new Reactive(target, a, null);
                            });
                            const result = value.apply(target, reactiveArgs);
                            listeners.forEach((l) => l.report(oldValue));
                            return result;
                        };
                    case 'pop':
                    case 'shift':
                    case 'reverse':
                    case 'sort':
                        return (...args) => {
                            const oldValue = [...target];
                            const result = value.apply(target, args);
                            listeners.forEach((l) => l.report(oldValue));
                            return result;
                        };
                    case 'splice':
                        return (start, deleteCount, ...items) => {
                            const oldValue = [...target];
                            const reactiveItems = items === null || items === void 0 ? void 0 : items.map((i) => {
                                if (i instanceof Reactive)
                                    return i;
                                return new Reactive(target, i, null);
                            });
                            const result = value.apply(target, [start, deleteCount, ...reactiveItems]);
                            listeners.forEach((l) => l.report(oldValue));
                            return result;
                        };
                    case '$$registerListener':
                        return (listener) => {
                            listeners.add(listener);
                        };
                    case '$$reactiveArray':
                        return true;
                    case '$$reactiveProxy':
                        return true;
                    default:
                        return value;
                }
                return value;
            },
            set(target, p, value) {
                return ReactiveObject.setReactiveValue(listeners, target, p, value);
            },
        });
    }
}

const delayedHooks = [];
let chainReactions = 0;
var Coordinator = {
    chainReactionStarted() {
        chainReactions += 1;
    },
    trackComponent(component, hook, oldValue) {
        delayedHooks.push({
            component,
            hook,
            oldValue,
        });
    },
    chainReactionEnded() {
        chainReactions -= 1;
        while (chainReactions <= 0 && !!delayedHooks.length) {
            const entry = delayedHooks.shift();
            entry.hook(entry.oldValue);
        }
    },
};

class Reactive {
    constructor(host, value, getter) {
        this.listeners = new Registry();
        this.computed = false;
        this.host = host;
        this.getter = getter;
        if (this.getter == null)
            this.set(value, true);
    }
    dirty() {
        if (this.computed) {
            this.computed = false;
            this.report(this.value);
        }
    }
    computeValue() {
        const callback = () => { this.dirty(); };
        Watcher.watch(this, callback, () => {
            const newValue = this.getter.call(this.host);
            this.set(newValue, false, true);
            this.computed = true;
        });
    }
    getValue() {
        if (this.getter != null && !this.computed)
            this.computeValue();
        return this.value;
    }
    get() {
        const watcher = Watcher.current;
        if (watcher != null) {
            const { context, callback } = watcher;
            this.registerHook(context, callback);
        }
        if (!this.computed && this.getter != null)
            this.computeValue();
        return this.getValue();
    }
    set(value, makeReactive, skipReport = false) {
        var _a;
        const oldValue = this.value;
        if (value == null && oldValue == null)
            return;
        const oldValueJSON = JSON.stringify(oldValue);
        const newValueJSON = JSON.stringify(value);
        if (oldValueJSON === newValueJSON)
            return;
        Coordinator.chainReactionStarted();
        if (makeReactive)
            this.updateValue(value);
        else
            this.value = value;
        if ((_a = this.value) === null || _a === void 0 ? void 0 : _a.$$registerListener)
            this.value.$$registerListener(this);
        if (!skipReport)
            this.$report(oldValue);
        else
            Coordinator.chainReactionEnded();
    }
    updateValue(value) {
        this.value = this.reactiveValue(value);
    }
    reactiveValue(value) {
        if (value == null)
            return null;
        if (Array.isArray(value)) {
            if (value.$$reactiveArray)
                return value;
            return new ReactiveArray(value);
        }
        if (typeof value === 'object' && !value.$$reactiveObject)
            return new ReactiveObject(value);
        return value;
    }
    registerHook(object, hook) {
        if (object !== this)
            this.listeners.register(object, hook);
    }
    report(oldValue) {
        Coordinator.chainReactionStarted();
        this.$report(oldValue);
    }
    $report(oldValue) {
        this.listeners.forEach((entity, hook) => {
            if (entity instanceof VivereComponent)
                Coordinator.trackComponent(entity, hook, oldValue);
            else
                hook(oldValue);
        });
        Coordinator.chainReactionEnded();
    }
    toJSON() {
        return JSON.stringify(this.value);
    }
}

class ReactiveHost {
    constructor() {
        this.$reactives = {};
    }
    $set(key, value, getter = null, setter = null) {
        if (typeof value === 'function' || key.startsWith('$') || key.startsWith('#'))
            return null;
        let reactive = this.$reactives[key];
        if (reactive == null) {
            reactive = new Reactive(this, value, getter);
            this.$reactives[key] = reactive;
            Object.defineProperty(this, key, {
                configurable: true,
                get() {
                    return reactive.get();
                },
                set(newValue) {
                    if (setter != null)
                        setter.bind(this)(newValue);
                    else if (getter != null)
                        throw new VivereError(`Cannot assign to computed property ${key}`);
                    else
                        reactive.set(newValue, true);
                },
            });
        }
        else
            this[key] = value;
        return reactive;
    }
}

const $parse = (entity, handler, seenKeys) => {
    const propertyDescriptors = Object.getOwnPropertyDescriptors(entity);
    const propertyDescriptorEntries = Object.entries(propertyDescriptors);
    propertyDescriptorEntries.forEach(([key, descriptor]) => {
        if (!seenKeys.includes(key)) {
            handler(key, descriptor);
            seenKeys.push(key);
        }
    });
};
var Properties = {
    parse(object, handler) {
        const seenKeys = [];
        $parse(object, handler, seenKeys);
        let prototype = Object.getPrototypeOf(object);
        while (prototype != null && prototype !== Object.prototype) {
            $parse(prototype, handler, seenKeys);
            prototype = Object.getPrototypeOf(prototype);
        }
    },
};

var _VivereComponent_instances, _VivereComponent_dataKeys, _VivereComponent_react, _VivereComponent_loadStoredData, _VivereComponent_reportCallback, _VivereComponent_dehydrateData;
const reservedKeywords = [
    'constructor',
];
class VivereComponent extends ReactiveHost {
    constructor(name, element, parent, renderController) {
        super();
        _VivereComponent_instances.add(this);
        _VivereComponent_dataKeys.set(this, new Set());
        this.$bindings = {};
        this.$children = [];
        this.$directives = new Set();
        this.$refs = {};
        this.$passed = {};
        this.$stored = {};
        this.$listeners = {};
        this.$isConnected = false;
        this.$isDehydrated = false;
        this.$isDestroyed = false;
        this.$name = name;
        this.$element = element;
        this.$parent = parent;
        this.$renderController = renderController;
        element.$component = this;
        if (parent != null)
            parent.$children.push(this);
    }
    $log(message) {
        console.log(message);
    }
    $setupReactivity() {
        Properties.parse(this, (key, descriptor) => {
            if (reservedKeywords.includes(key))
                return;
            const { get, set, value } = descriptor;
            this.$set(key, value, get, set);
        });
    }
    $set(key, value, getter = null, setter = null) {
        if (typeof value === 'function')
            return null;
        if (key.startsWith('$') || key.startsWith('#'))
            return null;
        const reactive = super.$set(key, Utility.jsonCopy(value), getter, setter);
        if (getter == null)
            __classPrivateFieldGet(this, _VivereComponent_dataKeys, "f").add(key);
        if (reactive == null)
            return null;
        reactive.registerHook(this, (oldValue) => __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_react).call(this, key, oldValue));
        return reactive;
    }
    $pass(key, expression, index) {
        var _a, _b;
        const { $passed, $reactives } = this;
        let definition = $passed[key];
        if (definition == null) {
            definition = {};
            $passed[key] = definition;
        }
        if (!((_a = definition.expression) === null || _a === void 0 ? void 0 : _a.length)) {
            delete $reactives[key];
            this.$set(key, null, () => {
                const { $parent } = this;
                const $definition = this.$passed[key];
                let value = Evaluator.parse($parent, $definition.expression);
                if ($definition.index != null)
                    value = value[$definition.index];
                return value;
            }, (value) => {
                const { $parent } = this;
                $parent.$set(expression, value);
            });
        }
        definition.expression = expression;
        definition.index = index;
        (_b = $reactives[key]) === null || _b === void 0 ? void 0 : _b.dirty();
    }
    $emit(event, arg) {
        const method = this.$bindings[event];
        if (method == null)
            throw new ComponentError(`Tried to emit unbound event: ${event}`, this);
        if (this.$parent[method] != null)
            this.$parent[method](arg);
        else
            throw new ComponentError(`Parent does not implement ${method}`, this);
    }
    $attach(html, ref) {
        const $ref = this.$refs[ref];
        if ($ref == null)
            throw new ComponentError(`No reference named ${ref} found`, this);
        let element;
        if ($ref instanceof Element)
            element = $ref;
        else
            element = $ref.$element;
        const tempNode = document.createElement('div');
        tempNode.innerHTML = html;
        Object.values(tempNode.children).forEach((child) => {
            element.appendChild(child);
            if (child instanceof HTMLElement)
                Walk.tree(child, this, this.$renderController);
        });
        this.$forceRender();
    }
    $attachElement(element, parent, before, renderController) {
        if (before != null)
            parent.insertBefore(element, before);
        else
            parent.appendChild(element);
        Walk.tree(element, this, renderController || this.$renderController);
        this.$forceRender();
    }
    $queueRender(directive) {
        Renderer.$queueRender(directive);
    }
    $nextRender(func) {
        Renderer.$nextRender(func);
    }
    $forceRender(shallow = false) {
        this.$directives.forEach((d) => Renderer.$queueRender(d));
        if (!shallow)
            this.$children.forEach((child) => child.$forceRender());
    }
    $addCallbackListener(callback, listener) {
        var _a;
        (_a = this.$listeners)[callback] || (_a[callback] = []);
        this.$listeners[callback].push(listener);
    }
    $removeCallbackListener(callback, listener) {
        var _a;
        this.$listeners[callback] = (_a = this.$listeners[callback]) === null || _a === void 0 ? void 0 : _a.filter((l) => l !== listener);
    }
    $setup() {
        const { beforeConnected } = this;
        beforeConnected === null || beforeConnected === void 0 ? void 0 : beforeConnected.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'beforeConnected');
        this.$setupReactivity();
    }
    $connect() {
        const { $isConnected, connected, rendered } = this;
        if ($isConnected)
            return;
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_loadStoredData).call(this);
        this.$forceRender.call(this, true);
        this.$isConnected = true;
        this.$isDehydrated = false;
        connected === null || connected === void 0 ? void 0 : connected.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'connected');
        this.$nextRender(() => {
            rendered === null || rendered === void 0 ? void 0 : rendered.call(this);
            __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'rendered');
        });
    }
    $destroy() {
        const { $children, $directives, $isDestroyed, $element, $parent, beforeDestroyed, destroyed } = this;
        if ($isDestroyed)
            return;
        beforeDestroyed === null || beforeDestroyed === void 0 ? void 0 : beforeDestroyed.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'beforeDestroyed');
        $directives.forEach((d) => d.destroy());
        $children.forEach((c) => c.$destroy());
        if ($parent != null) {
            const childIdx = $parent.$children.indexOf(this);
            if (childIdx >= 0)
                $parent.$children.splice(childIdx, 1);
        }
        ComponentRegistry.untrack(this);
        $element.parentNode.removeChild(this.$element);
        this.$isDestroyed = true;
        this.$isConnected = false;
        destroyed === null || destroyed === void 0 ? void 0 : destroyed.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'destroyed');
    }
    $dehydrate(shallow = false) {
        const { $children, $directives, $parent } = this;
        const { beforeDehydrated, beforeDestroyed, dehydrated, destroyed } = this;
        beforeDehydrated === null || beforeDehydrated === void 0 ? void 0 : beforeDehydrated.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'beforeDehydrated');
        beforeDestroyed === null || beforeDestroyed === void 0 ? void 0 : beforeDestroyed.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'beforeDestroyed');
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_dehydrateData).call(this);
        $directives.forEach((d) => { d.dehydrate(); d.destroy(); });
        if (!shallow)
            $children.forEach((c) => c.$dehydrate());
        if ($parent != null) {
            const childIdx = $parent.$children.indexOf(this);
            if (childIdx >= 0)
                $parent.$children.splice(childIdx, 1);
        }
        this.$isDehydrated = true;
        this.$isConnected = false;
        ComponentRegistry.untrack(this);
        dehydrated === null || dehydrated === void 0 ? void 0 : dehydrated.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'dehydrated');
        destroyed === null || destroyed === void 0 ? void 0 : destroyed.call(this);
        __classPrivateFieldGet(this, _VivereComponent_instances, "m", _VivereComponent_reportCallback).call(this, 'destroyed');
    }
}
_VivereComponent_dataKeys = new WeakMap(), _VivereComponent_instances = new WeakSet(), _VivereComponent_react = function _VivereComponent_react(key, oldValue) {
    const { $stored } = this;
    const storedDefinition = $stored[key];
    const methodName = `on${Utility.pascalCase(key)}Changed`;
    if (this[methodName] != null || storedDefinition != null) {
        const newValue = this[key];
        if (newValue == null && oldValue == null)
            return;
        if (newValue !== oldValue) {
            if (storedDefinition != null)
                Storage.save(key, storedDefinition, newValue);
            if (this[methodName] != null)
                this[methodName](newValue, oldValue);
        }
    }
}, _VivereComponent_loadStoredData = function _VivereComponent_loadStoredData() {
    Object.entries(this.$stored).forEach(([key, definition]) => {
        const storedValue = Storage.retrieve(key, definition);
        const defaultValue = definition.default || this[key];
        this.$set(key, storedValue || defaultValue);
    });
}, _VivereComponent_reportCallback = function _VivereComponent_reportCallback(callback) {
    var _a;
    (_a = this.$listeners[callback]) === null || _a === void 0 ? void 0 : _a.forEach((listener) => {
        listener();
    });
}, _VivereComponent_dehydrateData = function _VivereComponent_dehydrateData() {
    __classPrivateFieldGet(this, _VivereComponent_dataKeys, "f").forEach((key) => {
        const kebabKey = Utility.kebabCase(key);
        const value = this[key];
        const jsonValue = JSON.stringify(value);
        this.$element.setAttribute(`v-data:${kebabKey}`, jsonValue);
    });
};

const $definitions = new Registry();
var ComponentDefinitions = {
    register: (name, definition) => {
        let isValid = false;
        let prototype = definition;
        while (prototype != null && prototype !== Object.prototype) {
            if (prototype === VivereComponent) {
                isValid = true;
                break;
            }
            prototype = Object.getPrototypeOf(prototype);
        }
        if (!isValid)
            throw new VivereError(`Tried to register component ${name} that does not extend VivereComponent`);
        $definitions.register(name, definition);
    },
    getDefinition: (name) => {
        if (name == null || name.length <= 0)
            return VivereComponent;
        return $definitions.get(name);
    },
};

class ComponentDirective extends Directive {
    constructor() {
        super(...arguments);
        this.parsingComplete = false;
    }
    parse() {
        const { component, element, expression, key, renderController, modifiers } = this;
        const parent = component;
        const componentName = Utility.pascalCase(expression);
        const Definition = ComponentDefinitions.getDefinition(componentName);
        if (Definition == null)
            throw new VivereError(`Tried to instantiate unknown component ${componentName}`);
        this.component = new Definition(componentName, element, parent, renderController);
        this.component.$directives.add(this);
        if (key === 'defer') {
            this.suspendParsing();
            this.binding = this.completeParsing.bind(this);
            modifiers === null || modifiers === void 0 ? void 0 : modifiers.forEach((event) => {
                element.addEventListener(event, this.binding, true);
            });
        }
        else {
            this.parsingComplete = true;
            ComponentRegistry.track(this.component);
            this.hydrate();
        }
    }
    hydrate() {
        const { component } = this;
        component.$setup();
    }
    completeParsing() {
        var _a;
        if (this.parsingComplete)
            return true;
        this.parsingComplete = true;
        ComponentRegistry.track(this.component);
        this.resumeParsing();
        this.hydrate();
        Walk.tree(this.element, this.component, (_a = this.component) === null || _a === void 0 ? void 0 : _a.$renderController);
        this.component.$forceRender();
        this.removeDeferedEventListener();
        return true;
    }
    removeDeferedEventListener() {
        const { binding, element, modifiers } = this;
        if (binding != null)
            modifiers === null || modifiers === void 0 ? void 0 : modifiers.forEach((event) => {
                element.removeEventListener(event, binding, true);
            });
    }
    dehydrate() {
        this.removeDeferedEventListener();
        super.dehydrate();
    }
}
ComponentDirective.id = 'v-component';
ComponentDirective.needsComponent = false;

class BindDirective extends Directive {
    parse() {
        const { component, expression, key } = this;
        const camelKey = Utility.camelCase(key);
        component.$bindings[camelKey] = (expression || camelKey);
    }
}
BindDirective.id = 'v-bind';
BindDirective.forComponent = true;
BindDirective.requiresKey = true;

class ComputeDirective extends Directive {
    parse() {
        const { component, expression } = this;
        this.camelKey = Utility.camelCase(this.key);
        this.component.$set(this.camelKey, null, () => Evaluator.compute(component, expression));
    }
}
ComputeDirective.id = 'v-compute';
ComputeDirective.forComponent = true;
ComputeDirective.requiresKey = true;

class DataDirective extends Directive {
    parse() {
        const { expression } = this;
        let $expression;
        try {
            $expression = JSON.parse(expression);
        }
        catch (err) {
            $expression = Evaluator.parsePrimitive(expression);
        }
        if ($expression === undefined)
            $expression = expression;
        this.camelKey = Utility.camelCase(this.key);
        this.component.$set(this.camelKey, $expression);
    }
}
DataDirective.id = 'v-data';
DataDirective.forComponent = true;
DataDirective.requiresKey = true;
DataDirective.shouldRehydrate = false;

class MethodDirective extends Directive {
    parse() {
        const { component, expression } = this;
        this.camelKey = Utility.camelCase(this.key);
        this.component[this.camelKey] = (...args) => Evaluator.execute(component, expression, ...args);
    }
}
MethodDirective.id = 'v-method';
MethodDirective.forComponent = true;
MethodDirective.requiresKey = true;

class PassDirective extends Directive {
    parse() {
        const { component, expression } = this;
        const { $parent } = component;
        const key = Utility.camelCase(this.key);
        if ($parent == null)
            throw new DirectiveError('Cannot pass properties to a parentless component', this);
        let readKey;
        let idx;
        if (expression != null && expression.length > 0)
            readKey = expression;
        else
            readKey = key;
        if (readKey.match(/([a-zA-Z0-9-_]+)\[([0-9]+)\]/)) {
            const [$readKey, rest] = readKey.split('[');
            const [index] = rest.split(']');
            readKey = $readKey;
            idx = parseInt(index, 10);
        }
        component.$pass(key, readKey, idx);
    }
}
PassDirective.id = 'v-pass';
PassDirective.forComponent = true;
PassDirective.requiresKey = true;

class StoreDirective extends Directive {
    parse() {
        const { component, expression, key, modifiers } = this;
        let $expression;
        try {
            $expression = JSON.parse(expression);
        }
        catch (err) {
            $expression = Evaluator.parsePrimitive(expression);
        }
        if ($expression === undefined)
            $expression = expression;
        const camelKey = Utility.camelCase(key);
        const storageType = modifiers[0] || 'session';
        const storageModifier = modifiers[1] || null;
        component.$stored[camelKey] = {
            type: storageType,
            default: $expression,
            modifier: storageModifier,
        };
    }
}
StoreDirective.id = 'v-store';
StoreDirective.forComponent = true;
StoreDirective.requiresKey = true;
StoreDirective.shouldRehydrate = false;

class DisabledDirective$1 extends DisplayDirective {
    evaluateValue(value) {
        const { key, element } = this;
        if (value == null)
            element.removeAttribute(key);
        else
            element.setAttribute(key, value.toString());
    }
}
DisabledDirective$1.id = 'v-attr';
DisabledDirective$1.requiresKey = true;

var DOM = {
    conditionallyRender(host, show) {
        let newNode;
        if (show)
            newNode = host.element;
        else
            newNode = host.placeholder;
        if (newNode !== host.current) {
            host.container.replaceChild(newNode, host.current);
            host.current = newNode;
        }
    },
    toggleClass(element, className, add) {
        if (add)
            element.classList.add(className);
        else
            element.classList.remove(className);
    },
};

class ClassDirective$1 extends DisplayDirective {
    evaluateValue(value) {
        const { element, rawKey, lastValue } = this;
        if (rawKey) {
            const keyParts = rawKey.split('.');
            [...keyParts].forEach((className) => {
                DOM.toggleClass(element, className, !!value);
            });
        }
        else {
            if (Array.isArray(lastValue))
                lastValue.forEach((className) => {
                    DOM.toggleClass(element, className, false);
                });
            if (Array.isArray(value))
                value.forEach((className) => {
                    DOM.toggleClass(element, className, true);
                });
        }
    }
}
ClassDirective$1.id = 'v-class';

class DisabledDirective extends DisplayDirective {
    parse() {
        const { nodeName } = this.element;
        if (nodeName !== 'INPUT' && nodeName !== 'BUTTON')
            throw new DirectiveError(`Disabled directives only work on inputs and buttons, not ${nodeName}`, this);
    }
    evaluateValue(value) {
        this.element.disabled = !!value;
    }
}
DisabledDirective.id = 'v-disabled';

class HrefDirective extends DisplayDirective {
    parse() {
        const { nodeName } = this.element;
        if (nodeName !== 'A')
            throw new DirectiveError(`Href directives only work on anchor elements, not ${nodeName}`, this);
    }
    evaluateValue(value) {
        if (!value)
            this.element.removeAttribute('href');
        else
            this.element.href = value.toString();
    }
}
HrefDirective.id = 'v-href';

class HtmlDirective extends DisplayDirective {
    evaluateValue(value) {
        this.element.innerHTML = (value === null || value === void 0 ? void 0 : value.toString()) || null;
    }
}
HtmlDirective.id = 'v-html';

class AnimatorProperty {
    constructor(element, property) {
        this.element = element;
        this.property = property;
        this.defaultValue = element.style[property];
    }
    revert() {
        const { element, property, defaultValue } = this;
        element.style[property] = defaultValue;
    }
}

class AnimatableProperty extends AnimatorProperty {
    constructor(element, property, from, to) {
        super(element, property);
        this.origin = from;
        this.from = from;
        this.current = from;
        this.destination = to;
        this.to = to;
    }
    update(percentage) {
        const { element, property, from, to } = this;
        this.current = ((to - from) * percentage) + from;
        let styleValue = `${this.current}`;
        if (property !== 'opacity')
            styleValue += 'px';
        element.style[property] = styleValue;
    }
    reverse() {
        const { origin, current, destination } = this;
        this.from = current;
        this.to = origin;
        this.origin = destination;
        this.destination = origin;
    }
}

var _Animator_instances, _Animator_iterate;
const ANIMATION_DURATION = 150;
class Animator {
    constructor(element, vertical, callback) {
        _Animator_instances.add(this);
        this.running = false;
        this.element = element;
        this.vertical = vertical;
        this.callback = callback;
    }
    start(showing) {
        const { element, vertical } = this;
        this.showing = showing;
        const property = vertical ? 'height' : 'width';
        const currentPropertyValue = vertical ? element.offsetHeight : element.offsetWidth;
        const fromValue = showing ? 0 : currentPropertyValue;
        const toValue = showing ? currentPropertyValue : 0;
        this.property = new AnimatableProperty(element, property, fromValue, toValue);
        const computedStyle = window.getComputedStyle(element);
        const firstMarginProperty = vertical ? 'marginTop' : 'marginLeft';
        const currentFirstMarginValue = parseInt(computedStyle[firstMarginProperty], 10);
        const fromFirstMargin = showing ? 0 : currentFirstMarginValue;
        const toFirstMargin = showing ? currentFirstMarginValue : 0;
        this.firstMargin = new AnimatableProperty(element, firstMarginProperty, fromFirstMargin, toFirstMargin);
        const secondMarginProperty = vertical ? 'marginBottom' : 'marginRight';
        const currentSecondMarginValue = parseInt(computedStyle[secondMarginProperty], 10);
        const fromSecondMargin = showing ? 0 : currentSecondMarginValue;
        const toSecondMargin = showing ? currentSecondMarginValue : 0;
        this.secondMargin = new AnimatableProperty(element, secondMarginProperty, fromSecondMargin, toSecondMargin);
        const firstPaddingProperty = vertical ? 'paddingTop' : 'paddingLeft';
        const currentFirstPaddingValue = parseInt(computedStyle[firstPaddingProperty], 10);
        const fromFirstPadding = showing ? 0 : currentFirstPaddingValue;
        const toFirstPadding = showing ? currentFirstPaddingValue : 0;
        this.firstPadding = new AnimatableProperty(element, firstPaddingProperty, fromFirstPadding, toFirstPadding);
        const secondPaddingProperty = vertical ? 'paddingBottom' : 'paddingRight';
        const currentSecondPaddingValue = parseInt(computedStyle[secondPaddingProperty], 10);
        const fromSecondPadding = showing ? 0 : currentSecondPaddingValue;
        const toSecondPadding = showing ? currentSecondPaddingValue : 0;
        this.secondPadding = new AnimatableProperty(element, secondPaddingProperty, fromSecondPadding, toSecondPadding);
        const opacityProperty = 'opacity';
        const currentOpacityValue = parseFloat(computedStyle[opacityProperty]);
        const fromOpacity = showing ? 0 : currentOpacityValue;
        const toOpacity = showing ? currentOpacityValue : 0;
        this.opacity = new AnimatableProperty(element, opacityProperty, fromOpacity, toOpacity);
        const overflowProperty = vertical ? 'overflow-y' : 'overflow-x';
        this.overflow = new AnimatorProperty(element, overflowProperty);
        element.style[overflowProperty] = 'hidden';
        const transitionProperty = 'transitionDuration';
        this.transition = new AnimatorProperty(element, transitionProperty);
        element.style[transitionProperty] = '0ms';
        this.startTime = new Date().getTime();
        this.running = true;
        __classPrivateFieldGet(this, _Animator_instances, "m", _Animator_iterate).call(this);
    }
    get animatableProperties() {
        const { property, firstMargin, secondMargin, firstPadding, secondPadding, opacity, } = this;
        return [property, firstMargin, secondMargin, firstPadding, secondPadding, opacity].filter((p) => p != null);
    }
    get properties() {
        const { animatableProperties, overflow, transition, } = this;
        return [...animatableProperties, overflow, transition].filter((p) => p != null);
    }
    cancel() {
        if (this.frameRequest)
            cancelAnimationFrame(this.frameRequest);
        this.onComplete();
    }
    reverse() {
        const { showing, animatableProperties } = this;
        this.showing = !showing;
        animatableProperties.forEach((p) => p.reverse());
        this.startTime = new Date().getTime();
    }
    onComplete() {
        const { callback, showing, properties, } = this;
        properties.forEach((p) => p.revert());
        this.running = false;
        callback(showing);
    }
}
_Animator_instances = new WeakSet(), _Animator_iterate = function _Animator_iterate() {
    const { animatableProperties, startTime, } = this;
    const elapsedTime = new Date().getTime() - startTime;
    const percentageElapsed = elapsedTime / ANIMATION_DURATION;
    if (percentageElapsed < 1) {
        animatableProperties.forEach((p) => p.update(percentageElapsed));
        this.frameRequest = requestAnimationFrame(() => { __classPrivateFieldGet(this, _Animator_instances, "m", _Animator_iterate).call(this); });
    }
    else
        this.onComplete();
};

class ConditionalDirective extends DisplayDirective {
    constructor() {
        super(...arguments);
        this.evaluated = false;
        this.rendered = false;
        this.listeners = new Registry();
        this.awaitingRender = new Set();
    }
    parse() {
        const { element, modifiers } = this;
        const { parentElement } = element;
        this.container = parentElement;
        this.placeholder = document.createComment('');
        this.current = element;
        element.removeAttribute('hidden');
        this.suspendParsing();
        const shouldAnimateVertical = modifiers.includes('anim-y');
        const shouldAnimateHorizontal = modifiers.includes('anim-x');
        const shouldAnimate = shouldAnimateVertical || shouldAnimateHorizontal;
        if (shouldAnimate)
            this.animator = new Animator(element, shouldAnimateVertical, (showing) => {
                if (!showing)
                    DOM.conditionallyRender(this, false);
            });
    }
    evaluateValue(value) {
        const { component, element, evaluated, rendered } = this;
        const showing = !!value;
        if (this.lastValue === showing)
            return;
        const firstRender = !rendered;
        if (showing && firstRender) {
            this.rendered = true;
            this.resumeParsing();
            Walk.tree(element, component, this);
            component.$forceRender();
        }
        const { animator } = this;
        if (evaluated && animator) {
            const { running } = animator;
            if (showing && !running)
                DOM.conditionallyRender(this, true);
            if (!running)
                animator.start(showing);
            else
                animator.reverse();
        }
        else
            DOM.conditionallyRender(this, showing);
        if (value)
            this.awaitingRender.forEach((d) => {
                var _a;
                (_a = d.component) === null || _a === void 0 ? void 0 : _a.$queueRender(d);
                this.awaitingRender.delete(d);
            });
        this.listeners.forEach((listener, callback) => {
            callback();
        });
        this.evaluated = true;
    }
    shouldRender() {
        return this.lastValue;
    }
    dehydrate() {
        var _a;
        (_a = this.animator) === null || _a === void 0 ? void 0 : _a.cancel();
        DOM.conditionallyRender(this, true);
        super.dehydrate();
    }
}

class IfDirective extends ConditionalDirective {
}
IfDirective.id = 'v-if';

const showClass = 'hidden';
class ShowDirective extends DisplayDirective {
    evaluateValue(value) {
        const { element } = this;
        DOM.toggleClass(element, showClass, !value);
    }
}
ShowDirective.id = 'v-show';

class SrcDirective extends DisplayDirective {
    parse() {
        const { nodeName } = this.element;
        if (nodeName !== 'IMG')
            throw new DirectiveError(`Src directives only work on image elements, not ${nodeName}`, this);
    }
    evaluateValue(value) {
        const { element } = this;
        if (value == null)
            element.src = '';
        else
            element.src = value.toString();
    }
}
SrcDirective.id = 'v-src';

class ClassDirective extends DisplayDirective {
    evaluateValue(value) {
        const { element } = this;
        const key = Utility.camelCase(this.key);
        if (element instanceof HTMLElement)
            element.style[key] = value;
    }
}
ClassDirective.id = 'v-style';
ClassDirective.requiresKey = true;

class SyncDirective extends DisplayDirective {
    parse() {
        const { element } = this;
        const { nodeName } = element;
        const validNode = ['INPUT', 'SELECT', 'TEXTAREA'].includes(nodeName)
            || (['SPAN', 'P'].includes(nodeName) && element.contentEditable);
        if (!validNode)
            throw new DirectiveError(`Sync directives only work on input elements or contenteditable nodes, not ${nodeName}`, this);
        const isRadio = element instanceof HTMLInputElement && element.type === 'radio';
        this.event = isRadio ? 'change' : 'input';
        this.binding = this.sync.bind(this);
        this.element.addEventListener(this.event, this.binding);
        const initialValue = this.parseExpression();
        if (initialValue)
            this.evaluateValue(initialValue);
        else
            this.sync();
    }
    evaluateValue(value) {
        const { element } = this;
        let oldValue;
        if (element instanceof HTMLInputElement && element.type === 'checkbox') {
            oldValue = element.checked;
            element.checked = !!value;
        }
        else if (element instanceof HTMLInputElement && element.type === 'radio') {
            oldValue = element.checked;
            element.checked = element.value === (value === null || value === void 0 ? void 0 : value.toString());
        }
        else if (element instanceof HTMLParagraphElement || element instanceof HTMLSpanElement) {
            oldValue = element.innerText;
            const valueString = (value === null || value === void 0 ? void 0 : value.toString()) || null;
            if (element.innerText !== valueString)
                element.innerText = valueString;
        }
        else {
            oldValue = element.value;
            element.value = (value === null || value === void 0 ? void 0 : value.toString()) || null;
        }
        if ((oldValue === null || oldValue === void 0 ? void 0 : oldValue.toString()) !== (value === null || value === void 0 ? void 0 : value.toString())) {
            const event = new Event('input', {
                bubbles: true,
                cancelable: true,
            });
            element.dispatchEvent(event);
        }
    }
    destroy() {
        this.element.removeEventListener(this.event, this.binding);
    }
    value() {
        const { element } = this;
        if (element instanceof HTMLInputElement && ['checkbox', 'radio'].includes(element.type))
            return element.checked;
        if (element instanceof HTMLParagraphElement || element instanceof HTMLSpanElement)
            return element.innerText;
        return element.value;
    }
    sync() {
        const { element } = this;
        let inputValue = this.value();
        if (element instanceof HTMLInputElement && element.type === 'radio')
            if (inputValue)
                inputValue = element.value;
            else
                return true;
        Evaluator.assign(this.component, this.expression, inputValue);
        return true;
    }
}
SyncDirective.id = 'v-sync';

class TextDirective extends DisplayDirective {
    evaluateValue(value) {
        this.element.textContent = (value === null || value === void 0 ? void 0 : value.toString()) || null;
    }
}
TextDirective.id = 'v-text';

const listeners = {};
var EventBus = {
    register(event, listener) {
        if (listeners[event] == null)
            listeners[event] = new Set();
        listeners[event].add(listener);
    },
    deregister(event, listener) {
        var _a;
        (_a = listeners[event]) === null || _a === void 0 ? void 0 : _a.delete(listener);
    },
    broadcast(event, ...args) {
        var _a;
        (_a = listeners[event]) === null || _a === void 0 ? void 0 : _a.forEach((listener) => {
            listener(...args);
        });
    },
};

var Event$1 = {
    CLICK: 'click',
};

class EventDirective extends Directive {
    parse() {
        const { component, element, expression, key, modifiers } = this;
        if (expression == null && !((modifiers === null || modifiers === void 0 ? void 0 : modifiers.includes('prevent')) || (modifiers === null || modifiers === void 0 ? void 0 : modifiers.includes('cancel'))))
            throw new DirectiveError('Events must `prevent` or `cancel` to be valid without an expression', this, null);
        this.binding = this.execute.bind(this);
        const camelKey = Utility.camelCase(key);
        if (['beforeConnected', 'connected', 'rendered', 'beforeDehydrated', 'beforeDestroyed'].includes(camelKey))
            component.$addCallbackListener(camelKey, this.binding);
        else {
            this.clickOutsideBinding = this.handleClickOutside.bind(this);
            if (key === 'click' && (modifiers === null || modifiers === void 0 ? void 0 : modifiers.includes('outside')))
                EventBus.register(Event$1.CLICK, this.clickOutsideBinding);
            else
                element.addEventListener(key, this.binding);
        }
    }
    destroy() {
        var _a;
        (_a = this.component) === null || _a === void 0 ? void 0 : _a.$removeCallbackListener(this.key, this.binding);
        this.element.removeEventListener(this.key, this.binding);
        EventBus.deregister(Event$1.CLICK, this.clickOutsideBinding);
    }
    handleClickOutside(e) {
        if (e.target instanceof Element) {
            let clickTarget = e.target;
            do {
                if (clickTarget === this.element)
                    return;
                clickTarget = clickTarget.parentElement;
            } while (clickTarget != null);
        }
        this.binding(e);
    }
    execute(e) {
        const { expression, modifiers } = this;
        if (e instanceof KeyboardEvent && modifiers != null && modifiers.length > 0) {
            const keyCode = e.key || e.keyCode;
            const matchesModifier = modifiers.some((mod) => this.matchesKeycode(keyCode, mod));
            if (!matchesModifier)
                return undefined;
        }
        if (expression === null || expression === void 0 ? void 0 : expression.length)
            if (modifiers === null || modifiers === void 0 ? void 0 : modifiers.includes('delay'))
                setTimeout(() => this.executeEvent(e), 0);
            else
                this.executeEvent(e);
        else if (!(modifiers === null || modifiers === void 0 ? void 0 : modifiers.includes('cancel')) && !(modifiers === null || modifiers === void 0 ? void 0 : modifiers.includes('prevent')))
            throw new DirectiveError('Event directives require an expression, unless using the cancel or prevent flag', this);
        if (modifiers === null || modifiers === void 0 ? void 0 : modifiers.includes('prevent'))
            e.preventDefault();
        if (modifiers != null && modifiers.includes('cancel'))
            return false;
        return undefined;
    }
    executeEvent(e) {
        const { component, expression } = this;
        Evaluator.execute(component, expression, e);
    }
    matchesKeycode(keyCode, keyEvent) {
        switch (keyEvent) {
            case 'enter':
            case 'ent':
                return keyCode === 'Enter'
                    || keyCode === 13;
            case 'escape':
            case 'esc':
                return keyCode === 'Escape'
                    || keyCode === 27;
        }
        if (typeof keyCode === 'string')
            return keyEvent === keyCode.toLowerCase();
        return keyEvent === keyCode.toString();
    }
}
EventDirective.id = 'v-event';
EventDirective.requiresKey = true;

class HideDirective extends Directive {
}
HideDirective.id = 'v-hide';

class RefDirective extends Directive {
    parse() {
        const { component, element, expression } = this;
        if (this.onComponent()) {
            const { $parent } = component;
            if ($parent != null)
                $parent.$refs[expression] = component;
        }
        component.$refs[expression] = element;
    }
}
RefDirective.id = 'v-ref';

const isRenderController = (arg) => 'shouldRender' in arg;

class AntecedentConditionalDirective extends ConditionalDirective {
    parse() {
        var _a;
        const { element } = this;
        const { previousElementSibling } = element;
        const antecedent = (_a = previousElementSibling === null || previousElementSibling === void 0 ? void 0 : previousElementSibling.$directives) === null || _a === void 0 ? void 0 : _a.find((d) => d instanceof ConditionalDirective);
        if (antecedent instanceof ConditionalDirective)
            this.antecedent = antecedent;
        if (this.antecedent == null || this.antecedent instanceof ElseDirective)
            throw new DirectiveError(`${this.id()} directives require a preceeding v-if or v-else-if directive`, this);
        this.antecedent.listeners.register(this, this.renderCallback.bind(this));
        super.parse();
    }
    get antecedentValue() {
        let value = false;
        let { antecedent } = this;
        while (!value && antecedent != null) {
            value || (value = antecedent.lastValue);
            if (antecedent instanceof AntecedentConditionalDirective)
                antecedent = antecedent.antecedent;
            else
                antecedent = null;
        }
        return value;
    }
}

class ElseDirective extends AntecedentConditionalDirective {
    parseExpression() {
        return !this.antecedentValue;
    }
}
ElseDirective.id = 'v-else';

class ElseIfDirective extends AntecedentConditionalDirective {
    parseExpression() {
        const newValue = super.parseExpression();
        return newValue && !this.antecedentValue;
    }
}
ElseIfDirective.id = 'v-else-if';

const directives = [
    ForDirective,
    ComponentDirective,
    BindDirective,
    ComputeDirective,
    DataDirective,
    MethodDirective,
    PassDirective,
    RefDirective,
    StoreDirective,
    IfDirective,
    ElseIfDirective,
    ElseDirective,
    DisabledDirective$1,
    ClassDirective$1,
    DisabledDirective,
    EventDirective,
    HideDirective,
    HrefDirective,
    HtmlDirective,
    ShowDirective,
    SrcDirective,
    ClassDirective,
    SyncDirective,
    TextDirective,
];
const Walk = {
    tree(element, component, renderController) {
        Timer.time('Tree parsed', () => {
            Walk.element(element, component, renderController);
            ComponentRegistry.components.forEach((c) => { c.$connect(); });
        });
    },
    element(element, component, renderController) {
        const { attributes } = element;
        let $component = component;
        let $renderController = renderController;
        if (attributes['v-static'] != null)
            return;
        const parsedDirectives = [];
        Object.values(element.attributes).forEach((attr) => {
            const { name, value } = attr;
            if (name === 'class')
                return;
            directives.forEach((Dir) => {
                if (name.startsWith(Dir.id))
                    parsedDirectives.push({ Dir, name, value });
            });
        });
        if (parsedDirectives.length) {
            element.$directives = [];
            parsedDirectives.sort((a, b) => {
                const aIdx = directives.indexOf(a.Dir);
                const bIdx = directives.indexOf(b.Dir);
                if (aIdx > bIdx)
                    return 1;
                if (aIdx === bIdx)
                    return 0;
                return -1;
            });
            parsedDirectives.forEach(({ Dir, name, value }) => {
                var _a;
                const directive = new Dir(element, name, value, $component, $renderController);
                (_a = element.$directives) === null || _a === void 0 ? void 0 : _a.push(directive);
                $component = directive.component;
                if (isRenderController(directive))
                    $renderController = directive;
            });
        }
        if (!(element instanceof HTMLElement) || element.dataset[Directive.DATA_SUSPEND_PARSING] !== 'true')
            Walk.children(element, $component, $renderController);
    },
    children(element, component, renderController) {
        Object.values(element.children).forEach((child) => {
            Walk.element(child, component, renderController);
        });
    },
};

const $setup = (element) => {
    Timer.time('Document parsed', () => {
        Walk.element(element);
        ComponentRegistry.components.forEach((c) => { c.$connect(); });
    });
};
const $setupDocument = () => {
    $setup(document.body);
    document.removeEventListener('DOMContentLoaded', $setupDocument);
};
const dehydrate = () => {
    ComponentRegistry.components.forEach((c) => c.$dehydrate.call(c, true));
};
document.addEventListener('DOMContentLoaded', $setupDocument);
document.addEventListener('turbo:before-cache', () => {
    dehydrate();
});
document.addEventListener('turbo:before-render', (event) => {
    const { newBody } = event.detail;
    $setup(newBody);
    Renderer.$forceRender(false);
});
document.addEventListener('click', (e) => {
    EventBus.broadcast(Event$1.CLICK, e);
});
const Vivere = {
    register(name, definition) {
        ComponentDefinitions.register(name, definition);
    },
};

export { Vivere, VivereComponent };
//# sourceMappingURL=vivere.es6-esm.js.map
